export interface Flashcard {
  id: number;
  question: string;
  answer: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  explanation?: string;
}

export const flashcards: Flashcard[] = [
  {
    id: 1,
    question: "A legal relationship where an agent represents a client (principal).",
    answer: "Agency",
    category: "Agency",
    difficulty: "easy",
    explanation: "This relationship establishes a fiduciary duty from the agent to the client."
  },
  {
    id: 2,
    question: "An agent's legal duty to act in the client's best interest.",
    answer: "Fiduciary duty",
    category: "Agency",
    difficulty: "easy",
    explanation: "This is the highest standard of care owed to a client."
  },
  {
    id: 3,
    question: "What are the different types of agency relationships?",
    answer: "Seller, Buyer, Dual, Designated, and Transaction Broker.",
    category: "Agency",
    difficulty: "medium",
    explanation: "Each type defines whom the agent represents in a transaction."
  },
  {
    id: 4,
    question: "One agent or brokerage represents both buyer and seller.",
    answer: "Dual agency",
    category: "Agency",
    difficulty: "medium",
    explanation: "Requires informed written consent due to potential conflicts of interest."
  },
  {
    id: 5,
    question: "Broker appoints separate agents for buyer and seller in the same firm.",
    answer: "Designated agency",
    category: "Agency",
    difficulty: "medium",
    explanation: "Avoids the conflict of one agent representing both parties in an in-house sale."
  },
  {
    id: 6,
    question: "A non-agent who facilitates a transaction without representation.",
    answer: "Transaction broker",
    category: "Agency",
    difficulty: "medium",
    explanation: "Provides assistance to one or both parties but owes no fiduciary duties."
  },
  {
    id: 7,
    question: "How is an agency relationship typically created?",
    answer: "Through a written agreement (listing or buyer brokerage agreement).",
    category: "Agency",
    difficulty: "easy",
    explanation: "A written contract clearly defines the relationship, duties, and compensation."
  },
  {
    id: 8,
    question: "An agency relationship created by the actions or words of the parties.",
    answer: "Implied agency",
    category: "Agency",
    difficulty: "medium",
    explanation: "This should be avoided as it can lead to legal disputes due to unclear terms."
  },
  {
    id: 9,
    question: "A third party is led to believe an agency relationship exists by the principal's actions.",
    answer: "Ostensible agency (Agency by estoppel)",
    category: "Agency",
    difficulty: "hard",
    explanation: "The principal is legally prevented (estopped) from denying the agency relationship."
  },
  {
    id: 10,
    question: "How can an agency relationship be terminated?",
    answer: "Agreement, completion, expiration, death, or breach of contract.",
    category: "Agency",
    difficulty: "easy",
    explanation: "Termination can be mutual, planned (closing), or unplanned (breach)."
  },
  {
    id: 11,
    question: "The agent must put the client's interests above all others.",
    answer: "Loyalty",
    category: "Agency",
    difficulty: "easy",
    explanation: "The agent cannot have any undisclosed self-interest in the transaction."
  },
  {
    id: 12,
    question: "The agent must keep the client's private information confidential, forever.",
    answer: "Confidentiality",
    category: "Agency",
    difficulty: "medium",
    explanation: "This duty survives the termination of the agency relationship."
  },
  {
    id: 13,
    question: "The agent must obey all of the client's lawful instructions.",
    answer: "Obedience",
    category: "Agency",
    difficulty: "easy",
    explanation: "An agent cannot follow instructions that are illegal or unethical."
  },
  {
    id: 14,
    question: "The agent must disclose all known material facts to the client.",
    answer: "Disclosure",
    category: "Agency",
    difficulty: "easy",
    explanation: "Material facts are details that could influence the client's decisions."
  },
  {
    id: 15,
    question: "The agent must account for all money and property received.",
    answer: "Accounting",
    category: "Agency",
    difficulty: "easy",
    explanation: "This includes earnest money, which must be kept in a separate trust account."
  },
  {
    id: 16,
    question: "The agent must use their professional skills and knowledge competently.",
    answer: "Reasonable care and skill",
    category: "Agency",
    difficulty: "easy",
    explanation: "The agent must act as a prudent and knowledgeable professional."
  },
  {
    id: 17,
    question: "An agent authorized to handle all of a principal's affairs.",
    answer: "Universal agent",
    category: "Agency",
    difficulty: "medium",
    explanation: "This is rare in real estate and is typically created by a power of attorney."
  },
  {
    id: 18,
    question: "An agent authorized for a broad range of matters in a specific area.",
    answer: "General agent",
    category: "Agency",
    difficulty: "medium",
    explanation: "A property manager is a common example of a general agent."
  },
  {
    id: 19,
    question: "An agent authorized to perform a specific act or transaction.",
    answer: "Special agent",
    category: "Agency",
    difficulty: "medium",
    explanation: "A real estate agent hired to sell one property is a special agent."
  },
  {
    id: 20,
    question: "An agent appointed by another agent to help perform their duties.",
    answer: "Subagent",
    category: "Agency",
    difficulty: "medium",
    explanation: "A cooperating broker showing a property may be a subagent of the seller's broker."
  },
  {
    id: 21,
    question: "What is the difference between a client and a customer?",
    answer: "A client has an agency relationship (fiduciary duties); a customer does not.",
    category: "Agency",
    difficulty: "easy",
    explanation: "An agent works FOR a client and WITH a customer."
  },
  {
    id: 22,
    question: "The chain of events that leads to a sale.",
    answer: "Procuring cause",
    category: "Agency",
    difficulty: "medium",
    explanation: "This is used to determine which agent has earned the commission."
  },
  {
    id: 23,
    question: "A hidden defect that is not easily discoverable by inspection.",
    answer: "Latent defect",
    category: "Agency",
    difficulty: "easy",
    explanation: "Sellers and agents have a duty to disclose known latent defects."
  },
  {
    id: 24,
    question: "A defect that is obvious and can be seen with a normal inspection.",
    answer: "Patent defect",
    category: "Agency",
    difficulty: "easy",
    explanation: "There is no duty to disclose patent defects as they are presumed to be visible."
  },
  {
    id: 25,
    question: "A false statement of a material fact.",
    answer: "Misrepresentation",
    category: "Agency",
    difficulty: "medium",
    explanation: "This can be innocent, negligent, or fraudulent and can void a contract."
  },
  {
    id: 26,
    question: "What is the difference between active and passive fraud?",
    answer: "Active fraud is an intentional lie; passive fraud is intentional non-disclosure.",
    category: "Agency",
    difficulty: "medium",
    explanation: "Both are illegal. Passive fraud example: not revealing a known flooding issue."
  },
  {
    id: 27,
    question: "A legally enforceable agreement between two or more parties.",
    answer: "Contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It creates obligations that can be enforced by law if breached."
  },
  {
    id: 28,
    question: "What are the essential elements of a valid real estate contract?",
    answer: "Offer/Acceptance, Consideration, Competent Parties, Legal Purpose, In Writing.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "All five elements must be present for the contract to be enforceable."
  },
  {
    id: 29,
    question: "A definite offer by one party and an unqualified acceptance by another.",
    answer: "Offer and acceptance",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This creates a \"\"meeting of the minds\"\" or mutual assent."
  },
  {
    id: 30,
    question: "Something of legal value exchanged for a promise.",
    answer: "Consideration",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It can be money, property, or a service; it's the \"price\" of the promise."
  },
  {
    id: 31,
    question: "What does it mean for parties to be \"\"legally competent\"\"?",
    answer: "Of legal age (18 in GA) and of sound mind.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Contracts with minors or mentally incompetent persons are generally voidable."
  },
  {
    id: 32,
    question: "The contract's objective must be legal.",
    answer: "Legal purpose",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A contract for an illegal act (e.g., a drug house sale) is void."
  },
  {
    id: 33,
    question: "A law requiring certain contracts, including for real estate, to be in writing.",
    answer: "Statute of Frauds",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This is to prevent fraudulent claims about oral agreements."
  },
  {
    id: 34,
    question: "A contract where both parties exchange promises.",
    answer: "Bilateral contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A sales contract is bilateral: the seller promises to sell, the buyer promises to buy."
  },
  {
    id: 35,
    question: "A contract where one party promises to perform in exchange for an act.",
    answer: "Unilateral contract",
    category: "Contracts",
    difficulty: "medium",
    explanation: "An open listing is unilateral: the seller promises a commission if any agent finds a buyer."
  },
  {
    id: 36,
    question: "A contract where all parties have fulfilled their obligations.",
    answer: "Executed contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "The contract is executed at closing when the deed is delivered and payment is made."
  },
  {
    id: 37,
    question: "A contract where one or both parties still have obligations to perform.",
    answer: "Executory contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A sales contract is executory from its effective date until the closing date."
  },
  {
    id: 38,
    question: "A contract that meets all legal requirements and is enforceable.",
    answer: "Valid contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It contains all five essential elements."
  },
  {
    id: 39,
    question: "A contract with no legal force or effect from the beginning.",
    answer: "Void contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It lacks one of the essential elements, such as a legal purpose."
  },
  {
    id: 40,
    question: "A contract that can be legally canceled by one of the parties.",
    answer: "Voidable contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A contract signed by a minor is voidable by that minor."
  },
  {
    id: 41,
    question: "A valid contract that cannot be enforced in court.",
    answer: "Unenforceable contract",
    category: "Contracts",
    difficulty: "medium",
    explanation: "An oral real estate contract is unenforceable due to the Statute of Frauds."
  },
  {
    id: 42,
    question: "The transfer of contractual rights or duties to a third party.",
    answer: "Assignment of a contract",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Most contracts are assignable unless stated otherwise."
  },
  {
    id: 43,
    question: "The substitution of a new contract or party for an old one, releasing the original party.",
    answer: "Novation",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Novation requires the agreement of all parties involved."
  },
  {
    id: 44,
    question: "Failure to perform contractual obligations without a legal excuse.",
    answer: "Breach of contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This gives the non-breaching party legal remedies."
  },
  {
    id: 45,
    question: "What are the remedies for a breach of contract?",
    answer: "Specific performance, monetary damages, rescission, or liquidated damages.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "The remedy depends on the contract and the nature of the breach."
  },
  {
    id: 46,
    question: "A court order forcing the breaching party to perform the contract.",
    answer: "Specific performance",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Common for sellers who breach, as real estate is considered unique."
  },
  {
    id: 47,
    question: "A pre-agreed amount of damages for a breach.",
    answer: "Liquidated damages",
    category: "Contracts",
    difficulty: "medium",
    explanation: "The buyer's earnest money often serves as liquidated damages for the seller."
  },
  {
    id: 48,
    question: "The cancellation of a contract, returning parties to their original positions.",
    answer: "Rescission",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Any money or property exchanged is returned."
  },
  {
    id: 49,
    question: "A contract giving a buyer the right to purchase at a set price and term.",
    answer: "Option contract",
    category: "Contracts",
    difficulty: "medium",
    explanation: "The buyer pays a fee for the option but is not obligated to buy."
  },
  {
    id: 50,
    question: "A condition that must be met for a contract to become binding.",
    answer: "Contingency clause",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Common contingencies include financing, inspection, and appraisal."
  },
  {
    id: 51,
    question: "A change to an existing contract's terms.",
    answer: "Amendment",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It must be in writing and signed by both parties."
  },
  {
    id: 52,
    question: "An additional document attached to and made part of a contract.",
    answer: "Addendum",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It adds new terms or information to the original agreement."
  },
  {
    id: 53,
    question: "A clause making all contract dates and deadlines strictly enforceable.",
    answer: "Time is of the essence clause",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Failure to perform on time is a material breach."
  },
  {
    id: 54,
    question: "A contract between a property owner and a broker to find a buyer.",
    answer: "Listing agreement",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This agreement creates the agency relationship."
  },
  {
    id: 55,
    question: "A listing where the broker earns a commission regardless of who sells the property.",
    answer: "Exclusive right-to-sell listing",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This is the most common type and offers the broker the most protection."
  },
  {
    id: 56,
    question: "The broker earns a commission unless the owner sells the property themselves.",
    answer: "Exclusive agency listing",
    category: "Contracts",
    difficulty: "medium",
    explanation: "The owner retains the right to sell without paying a commission."
  },
  {
    id: 57,
    question: "A non-exclusive listing where multiple brokers can be hired.",
    answer: "Open listing",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Only the broker who is the procuring cause of the sale gets paid."
  },
  {
    id: 58,
    question: "A document that pledges property as security for a loan.",
    answer: "Mortgage",
    category: "Financing",
    difficulty: "easy",
    explanation: "This creates a lien on the property in favor of the lender."
  },
  {
    id: 59,
    question: "A written promise to repay a debt with interest.",
    answer: "Promissory note",
    category: "Financing",
    difficulty: "easy",
    explanation: "It is the evidence of the loan, while the mortgage is the security."
  },
  {
    id: 60,
    question: "The borrower.",
    answer: "Mortgagor",
    category: "Financing",
    difficulty: "easy",
    explanation: "The mortgagor GIVES the mortgage."
  },
  {
    id: 61,
    question: "The lender.",
    answer: "Mortgagee",
    category: "Financing",
    difficulty: "easy",
    explanation: "The mortgagee RECEIVES the mortgage."
  },
  {
    id: 62,
    question: "The process of paying off a loan in installments over time.",
    answer: "Amortization",
    category: "Financing",
    difficulty: "easy",
    explanation: "Each payment includes both principal and interest."
  },
  {
    id: 63,
    question: "Principal, Interest, Taxes, and Insurance.",
    answer: "PITI",
    category: "Financing",
    difficulty: "easy",
    explanation: "These are the four components of a monthly mortgage payment."
  },
  {
    id: 64,
    question: "The ratio of the loan amount to the property's value or price.",
    answer: "Loan-to-value ratio (LTV)",
    category: "Financing",
    difficulty: "easy",
    explanation: "LTV = Loan Amount / Property Value."
  },
  {
    id: 65,
    question: "A loan not insured or guaranteed by the government (FHA, VA).",
    answer: "Conventional loan",
    category: "Financing",
    difficulty: "easy",
    explanation: "It typically requires a larger down payment and higher credit score."
  },
  {
    id: 66,
    question: "Insurance for the lender on conventional loans with an LTV over 80%.",
    answer: "Private mortgage insurance (PMI)",
    category: "Financing",
    difficulty: "easy",
    explanation: "It protects the lender if the borrower defaults."
  },
  {
    id: 67,
    question: "A loan insured by the Federal Housing Administration.",
    answer: "FHA loan",
    category: "Financing",
    difficulty: "easy",
    explanation: "Allows for lower down payments and is accessible to borrowers with lower credit scores."
  },
  {
    id: 68,
    question: "A loan guaranteed by the Department of Veterans Affairs for eligible veterans.",
    answer: "VA loan",
    category: "Financing",
    difficulty: "easy",
    explanation: "Often requires no down payment and has no PMI."
  },
  {
    id: 69,
    question: "A government loan for properties in eligible rural areas.",
    answer: "USDA loan",
    category: "Financing",
    difficulty: "easy",
    explanation: "Often requires no down payment."
  },
  {
    id: 70,
    question: "A loan with an interest rate that can change periodically.",
    answer: "Adjustable-rate mortgage (ARM)",
    category: "Financing",
    difficulty: "easy",
    explanation: "The rate is tied to a specific economic index."
  },
  {
    id: 71,
    question: "A loan with an interest rate that remains the same for the entire term.",
    answer: "Fixed-rate mortgage",
    category: "Financing",
    difficulty: "easy",
    explanation: "This provides a stable and predictable monthly payment."
  },
  {
    id: 72,
    question: "An upfront fee paid to the lender to lower the interest rate.",
    answer: "Discount points",
    category: "Financing",
    difficulty: "easy",
    explanation: "One point equals 1% of the loan amount."
  },
  {
    id: 73,
    question: "A fee charged for paying off a loan early.",
    answer: "Prepayment penalty",
    category: "Financing",
    difficulty: "easy",
    explanation: "These are now rare on most residential loans."
  },
  {
    id: 74,
    question: "A federal law requiring lenders to disclose the true cost of credit (APR).",
    answer: "Truth in Lending Act (TILA)",
    category: "Financing",
    difficulty: "medium",
    explanation: "This helps consumers compare loan offers from different lenders."
  },
  {
    id: 75,
    question: "The effective cost of a loan, expressed as a yearly rate.",
    answer: "Annual Percentage Rate (APR)",
    category: "Financing",
    difficulty: "medium",
    explanation: "It includes the interest rate plus points and other loan fees."
  },
  {
    id: 76,
    question: "A federal law that prohibits kickbacks and requires disclosure of settlement costs.",
    answer: "Real Estate Settlement Procedures Act (RESPA)",
    category: "Financing",
    difficulty: "medium",
    explanation: "It protects consumers from unearned fees and excessive closing costs."
  },
  {
    id: 77,
    question: "A rule creating two new disclosure forms: the Loan Estimate and Closing Disclosure.",
    answer: "TILA-RESPA Integrated Disclosure (TRID) rule",
    category: "Financing",
    difficulty: "medium",
    explanation: "These forms simplify and clarify the loan and closing process for consumers."
  },
  {
    id: 78,
    question: "A federal law that prohibits discrimination in lending.",
    answer: "Equal Credit Opportunity Act (ECOA)",
    category: "Financing",
    difficulty: "medium",
    explanation: "Lenders cannot discriminate based on race, religion, sex, age, etc."
  },
  {
    id: 79,
    question: "An intermediary who connects borrowers with lenders.",
    answer: "Mortgage broker",
    category: "Financing",
    difficulty: "easy",
    explanation: "They do not lend their own money."
  },
  {
    id: 80,
    question: "A direct lender who originates and funds their own loans.",
    answer: "Mortgage banker",
    category: "Financing",
    difficulty: "easy",
    explanation: "They may service the loan or sell it on the secondary market."
  },
  {
    id: 81,
    question: "A market where existing mortgages are bought and sold.",
    answer: "Secondary mortgage market",
    category: "Financing",
    difficulty: "medium",
    explanation: "Provides liquidity to primary lenders. Key players are Fannie Mae and Freddie Mac."
  },
  {
    id: 82,
    question: "A government-sponsored enterprise (GSE) that buys conventional mortgages.",
    answer: "Fannie Mae (FNMA)",
    category: "Financing",
    difficulty: "medium",
    explanation: "It is the largest buyer of mortgages in the secondary market."
  },
  {
    id: 83,
    question: "A GSE that buys and guarantees conventional mortgages.",
    answer: "Freddie Mac (FHLMC)",
    category: "Financing",
    difficulty: "medium",
    explanation: "It functions like Fannie Mae to provide liquidity to the mortgage market."
  },
  {
    id: 84,
    question: "A government corporation that guarantees government-insured loans (FHA, VA).",
    answer: "Ginnie Mae (GNMA)",
    category: "Financing",
    difficulty: "medium",
    explanation: "Ginnie Mae does not buy loans; it guarantees mortgage-backed securities."
  },
  {
    id: 85,
    question: "A clause allowing the lender to demand full repayment if the borrower defaults.",
    answer: "Acceleration clause",
    category: "Financing",
    difficulty: "easy",
    explanation: "This is a standard clause in most loan documents."
  },
  {
    id: 86,
    question: "A clause that voids the lender's lien when the loan is fully paid.",
    answer: "Defeasance clause",
    category: "Financing",
    difficulty: "medium",
    explanation: "This ensures the borrower receives clear title upon repayment."
  },
  {
    id: 87,
    question: "Land and everything permanently attached to it, plus ownership rights.",
    answer: "Real property",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "This includes surface, subsurface (minerals), and air rights."
  },
  {
    id: 88,
    question: "All property that is not real property; movable items.",
    answer: "Personal property (chattel)",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "Examples include furniture, cars, and freestanding appliances."
  },
  {
    id: 89,
    question: "Personal property that has been permanently attached and become real property.",
    answer: "Fixture",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "The MARIA test is used to determine if an item is a fixture."
  },
  {
    id: 90,
    question: "The rights of ownership: Possession, Control, Enjoyment, Exclusion, Disposition.",
    answer: "Bundle of legal rights",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "These rights describe what an owner can do with their property."
  },
  {
    id: 91,
    question: "The highest and most complete form of property ownership.",
    answer: "Fee simple absolute",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "It is indefinite in duration, transferable, and inheritable."
  },
  {
    id: 92,
    question: "An ownership interest that can be terminated if a specific event occurs.",
    answer: "Fee simple defeasible",
    category: "Property Ownership",
    difficulty: "hard",
    explanation: "Title can revert to the original grantor or a third party."
  },
  {
    id: 93,
    question: "An interest in property that lasts for the lifetime of a designated person.",
    answer: "Life estate",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Upon death, the property passes to a remainderman or reverts to the grantor."
  },
  {
    id: 94,
    question: "A leasehold for a specific, fixed period of time.",
    answer: "Estate for years",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "It has a definite start and end date and does not automatically renew."
  },
  {
    id: 95,
    question: "A leasehold that automatically renews for successive periods (e.g., month-to-month).",
    answer: "Estate from period to period (periodic tenancy)",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "It continues until one party gives proper notice of termination."
  },
  {
    id: 96,
    question: "Ownership of property by one person or entity alone.",
    answer: "Tenancy in severalty",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "The owner's interest is \"\"severed\"\" from all others."
  },
  {
    id: 97,
    question: "Co-ownership where two or more people have an undivided interest, with no survivorship rights.",
    answer: "Tenancy in common",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Interests can be unequal, and a deceased owner's share goes to their heirs."
  },
  {
    id: 98,
    question: "Co-ownership with the right of survivorship.",
    answer: "Joint tenancy",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Requires the four unities: time, title, interest, and possession (TTIP)."
  },
  {
    id: 99,
    question: "A special form of joint tenancy for married couples (not used in Georgia).",
    answer: "Tenancy by the entirety",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Provides protection against creditors of only one spouse."
  },
  {
    id: 100,
    question: "Individual ownership of a unit, plus shared ownership of common elements.",
    answer: "Condominium",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "Owners hold fee simple title to their unit and are tenants in common for shared areas."
  },
  {
    id: 101,
    question: "Ownership of shares of stock in a corporation that owns a building.",
    answer: "Cooperative (co-op)",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Shareholders receive a proprietary lease for their specific unit."
  },
  {
    id: 102,
    question: "The right to use another person's land for a specific purpose.",
    answer: "Easement",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "A common example is a utility easement for power lines."
  },
  {
    id: 103,
    question: "An easement that benefits a specific parcel of land (dominant tenement).",
    answer: "Easement appurtenant",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "It \"\"runs with the land,\"\" meaning it transfers with the title."
  },
  {
    id: 104,
    question: "An easement that benefits a person or company, not a parcel of land.",
    answer: "Easement in gross",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "A utility easement is a common example."
  },
  {
    id: 105,
    question: "How are easements created?",
    answer: "By grant, necessity, prescription (adverse use), or implication.",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "An easement by necessity may be created for a landlocked parcel."
  },
  {
    id: 106,
    question: "An unauthorized physical intrusion onto another person's property.",
    answer: "Encroachment",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "A fence built over the property line is an encroachment."
  },
  {
    id: 107,
    question: "A claim against a property that serves as security for a debt.",
    answer: "Lien",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "A mortgage is a voluntary lien; a tax lien is an involuntary lien."
  },
  {
    id: 108,
    question: "What is the difference between a general and specific lien?",
    answer: "A general lien affects all property; a specific lien affects only one property.",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "An income tax lien is general; a mortgage is specific."
  },
  {
    id: 109,
    question: "A lien for unpaid labor or materials used to improve a property.",
    answer: "Mechanic's lien",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "Protects the payment rights of contractors and suppliers."
  },
  {
    id: 110,
    question: "Acquiring title to property through open, notorious, and hostile possession for a statutory period.",
    answer: "Adverse possession",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Sometimes called \"\"squatter's rights.\"\" In GA, the period is 20 years."
  },
  {
    id: 111,
    question: "The government's right to take private property for public use with just compensation.",
    answer: "Eminent domain",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "The process of taking the property is called condemnation."
  },
  {
    id: 112,
    question: "The process of estimating the market value of a property.",
    answer: "Valuation (Appraisal)",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Also known as appraisal."
  },
  {
    id: 113,
    question: "The most probable price a property should sell for in an open, competitive market.",
    answer: "Market value",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "It assumes a willing buyer and seller, both acting knowledgeably."
  },
  {
    id: 114,
    question: "The four characteristics of value: Demand, Utility, Scarcity, and Transferability.",
    answer: "DUST",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "All four must be present for a property to have value."
  },
  {
    id: 115,
    question: "A buyer will not pay more for a property than the cost of a similar substitute.",
    answer: "Principle of substitution",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "This is the basis for the sales comparison approach."
  },
  {
    id: 116,
    question: "The most profitable, legally permitted, and physically possible use of a property.",
    answer: "Principle of highest and best use",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "An appraiser must determine this to find the property's true value."
  },
  {
    id: 117,
    question: "What are the three main approaches to value?",
    answer: "Sales Comparison Approach, Cost Approach, and Income Approach.",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Appraisers often use more than one approach and reconcile the results."
  },
  {
    id: 118,
    question: "An approach that compares the subject property to recently sold similar properties (\"\"comps\"\").",
    answer: "Sales Comparison Approach (Market Data Approach)",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "This is the most common approach for residential properties."
  },
  {
    id: 119,
    question: "An agent's analysis of market data to help determine a listing or offering price.",
    answer: "Comparative Market Analysis (CMA)",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "It is similar to an appraisal but less formal and cannot be called an appraisal."
  },
  {
    id: 120,
    question: "Value = (Replacement Cost - Depreciation) + Land Value.",
    answer: "Cost Approach",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Most useful for unique properties like schools or new construction."
  },
  {
    id: 121,
    question: "What is the difference between reproduction cost and replacement cost?",
    answer: "Reproduction is an exact replica; replacement is a building with similar utility.",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Replacement cost (using modern materials) is more common."
  },
  {
    id: 122,
    question: "A loss in value for any reason.",
    answer: "Depreciation",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "There are three types: physical, functional, and external."
  },
  {
    id: 123,
    question: "A loss in value from wear and tear or deferred maintenance.",
    answer: "Physical deterioration",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "A leaky roof is an example. Can be curable or incurable."
  },
  {
    id: 124,
    question: "A loss in value from outdated design or features.",
    answer: "Functional obsolescence",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "A 5-bedroom house with one bathroom is an example. Can be curable or incurable."
  },
  {
    id: 125,
    question: "A loss in value from factors outside the property.",
    answer: "External (economic) obsolescence",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Examples: proximity to a landfill or high crime rates. Almost always incurable."
  },
  {
    id: 126,
    question: "An approach that values a property based on the income it generates.",
    answer: "Income Approach",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Used for commercial and investment properties."
  },
  {
    id: 127,
    question: "Value = Net Operating Income (NOI) / Capitalization Rate (Cap Rate).",
    answer: "IRV Formula",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Remember IRV: Income / Rate = Value."
  },
  {
    id: 128,
    question: "A property's income after subtracting vacancy losses and operating expenses.",
    answer: "Net Operating Income (NOI)",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Debt service (mortgage payments) is NOT included in operating expenses."
  },
  {
    id: 129,
    question: "The expected rate of return on an investment property.",
    answer: "Capitalization Rate (Cap Rate)",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Cap Rate = NOI / Value. A higher cap rate generally means higher risk."
  },
  {
    id: 130,
    question: "A simple method to estimate value for 1-4 unit residential properties.",
    answer: "Gross Rent Multiplier (GRM)",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Value = GRM x Gross Annual Rent."
  },
  {
    id: 131,
    question: "If a property is valued at $350,000 and a lender requires an 80% LTV, what is the loan amount?",
    answer: "$280,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "LTV (Loan-to-Value) ratio shows what percentage of the property value can be financed. Step 1: Convert 80% LTV to a decimal: 0.80. Step 2: Multiply the property value by the LTV ratio: $350,000 × 0.80 = $280,000. This means the lender will finance $280,000, and the borrower must provide a down payment of $70,000 (20% of $350,000)."
  },
  {
    id: 132,
    question: "A borrower is paying 2 discount points on a $250,000 loan. How much will the points cost?",
    answer: "$5,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "One point equals 1% of the loan amount. Step 1: Convert 2 points to a decimal: 2 points = 2% = 0.02. Step 2: Multiply the loan amount by the decimal: $250,000 × 0.02 = $5,000. Points are fees paid to the lender at closing, typically used to 'buy down' the interest rate (discount points) or as an origination fee. One point on a $250,000 loan = $2,500, so 2 points = $5,000."
  },
  {
    id: 133,
    question: "A property sold for $400,000. The commission rate is 6%. What is the total commission?",
    answer: "$24,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Total commission = Sales Price × Commission Rate (as a decimal). Step 1: Convert 6% to a decimal: 6% = 0.06. Step 2: Multiply the sales price by the commission rate: $400,000 × 0.06 = $24,000. The commission rate is always negotiable and is typically paid by the seller at closing. If the commission is split between listing and selling brokers, each would receive a portion of the $24,000."
  },
  {
    id: 134,
    question: "Annual property taxes are $3,600 and closing is on June 15th (seller's 165th day). What is the seller's prorated share? (360-day year).",
    answer: "$1,650",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "Proration divides expenses between buyer and seller based on ownership days. This problem uses a 360-day year (common in real estate). Step 1: Calculate daily rate: $3,600 ÷ 360 = $10 per day. Step 2: The seller owned the property for 165 days (January 1 through June 15, with June 15 typically belonging to the buyer, but counted here for the seller). Step 3: Multiply daily rate by seller's days: $10 × 165 = $1,650. The seller is debited (owes) $1,650, and the buyer is credited (receives) $1,650 since they'll be responsible for the remaining 195 days of the year."
  },
  {
    id: 135,
    question: "A property has an NOI of $50,000 and the market cap rate is 8%. What is the estimated value?",
    answer: "$625,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "The capitalization (cap) rate method estimates property value using the income approach. Formula: Value = NOI ÷ Cap Rate. NOI (Net Operating Income) is income after operating expenses but before debt service and taxes. Step 1: Convert 8% cap rate to a decimal: 0.08. Step 2: Divide NOI by cap rate: $50,000 ÷ 0.08 = $625,000. A higher cap rate indicates higher risk or lower value; a lower cap rate indicates lower risk or higher value. This method is commonly used for income-producing properties."
  },
  {
    id: 136,
    question: "A property has a gross annual rent of $60,000 and the area GRM is 10. What is the estimated value?",
    answer: "$600,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "GRM (Gross Rent Multiplier) is a simple valuation method for residential properties (1-4 units). Formula: Value = GRM × Gross Annual Rent. The GRM is found by comparing similar properties: GRM = Sales Price ÷ Gross Annual Rent. Step 1: Multiply the GRM by the gross annual rent: 10 × $60,000 = $600,000. This method is quick but less accurate than the cap rate method because it doesn't account for operating expenses. It's useful for quick estimates and comparing similar properties in the same area."
  },
  {
    id: 137,
    question: "How many square feet are in one acre?",
    answer: "43,560 square feet",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "One acre equals 43,560 square feet. This is a standard measurement that must be memorized for real estate exams. Useful conversions: 1 acre = 43,560 sq ft, 1 acre = 4,840 sq yards, 1 section = 640 acres, 1 township = 36 sections. To convert square feet to acres, divide by 43,560. Example: 87,120 sq ft ÷ 43,560 = 2 acres."
  },
  {
    id: 138,
    question: "A rectangular lot measures 150 feet by 200 feet. How many acres is it?",
    answer: "~0.69 acres",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Step 1: Calculate the area in square feet: 150 feet × 200 feet = 30,000 square feet. Step 2: Convert square feet to acres by dividing by 43,560 (the number of square feet in one acre): 30,000 ÷ 43,560 = 0.6887 acres, which rounds to approximately 0.69 acres. Formula: Area (sq ft) ÷ 43,560 = Acres. This calculation is essential for determining lot sizes and property descriptions."
  },
  {
    id: 139,
    question: "A buyer's PITI is $1,800/mo. Annual taxes are $2,400 and insurance is $1,200. What is the monthly P&I?",
    answer: "$1,500",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "PITI stands for Principal, Interest, Taxes, and Insurance - the total monthly mortgage payment. To find P&I (Principal and Interest only), subtract the monthly Taxes and Insurance from PITI. Step 1: Calculate monthly Taxes and Insurance: ($2,400 + $1,200) ÷ 12 = $3,600 ÷ 12 = $300 per month. Step 2: Subtract monthly T&I from PITI: $1,800 - $300 = $1,500. So the monthly Principal and Interest payment is $1,500. This is the portion that goes toward paying down the loan and interest."
  },
  {
    id: 140,
    question: "What is the formula for the area of a triangle?",
    answer: "Area = (1/2) x Base x Height",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "The area of a triangle is calculated as: Area = (1/2) × Base × Height, or Area = Base × Height ÷ 2. The base and height must be perpendicular to each other. This formula is used for calculating the area of irregularly shaped lots that can be divided into triangular sections, or for triangular parcels. Example: A triangle with a base of 100 feet and height of 80 feet has an area of (1/2) × 100 × 80 = 4,000 square feet."
  },
  {
    id: 141,
    question: "The legal concept of property ownership rights.",
    answer: "Title",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "It is not a physical document."
  },
  {
    id: 142,
    question: "The written legal instrument that transfers title to real property.",
    answer: "Deed",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "The deed is the evidence of the transfer of title."
  },
  {
    id: 143,
    question: "The seller/giver of property.",
    answer: "Grantor",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "The grantOR gives."
  },
  {
    id: 144,
    question: "The buyer/receiver of property.",
    answer: "Grantee",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "The grantEE receives."
  },
  {
    id: 145,
    question: "What are the essential elements of a valid deed?",
    answer: "Written, competent grantor, identifiable grantee, words of conveyance, legal description, grantor's signature, delivery & acceptance.",
    category: "Transfer of Title",
    difficulty: "medium",
    explanation: "All of these elements are required for a valid conveyance."
  },
  {
    id: 146,
    question: "A deed providing the greatest protection to the grantee, warranting title forever.",
    answer: "General warranty deed",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "The grantor warrants the title against all defects, forever."
  },
  {
    id: 147,
    question: "A deed where the grantor warrants the title only during their period of ownership.",
    answer: "Special warranty deed",
    category: "Transfer of Title",
    difficulty: "medium",
    explanation: "The grantor does not cover defects from before their ownership."
  },
  {
    id: 148,
    question: "A deed that conveys any interest the grantor may have, with no warranties.",
    answer: "Quitclaim deed",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Offers the least protection. Often used to clear title defects (\"\"clouds\"\")."
  },
  {
    id: 149,
    question: "The final step in transferring title, when the deed is given and accepted.",
    answer: "Delivery and acceptance",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Title officially passes at this moment."
  },
  {
    id: 150,
    question: "Notice given to the world by recording a document in the public records.",
    answer: "Constructive notice",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Recording a deed provides constructive notice of ownership."
  },
  {
    id: 151,
    question: "Direct, express knowledge of a fact.",
    answer: "Actual notice",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Being told about a lien or seeing someone live on the property is actual notice."
  },
  {
    id: 152,
    question: "The complete history of a property's ownership.",
    answer: "Chain of title",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "A title search establishes the chain of title."
  },
  {
    id: 153,
    question: "Any claim or encumbrance that makes the title's validity doubtful.",
    answer: "Cloud on the title",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "A quitclaim deed is often used to resolve a cloud on the title."
  },
  {
    id: 154,
    question: "Insurance that protects against losses from past, undiscovered title defects.",
    answer: "Title insurance",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Protects the policyholder (owner or lender) from prior title issues."
  },
  {
    id: 155,
    question: "What is the difference between an owner's policy and a lender's policy of title insurance?",
    answer: "An owner's policy protects the owner's equity; a lender's policy protects the lender's loan amount.",
    category: "Transfer of Title",
    difficulty: "medium",
    explanation: "The lender's policy amount decreases as the loan is paid off."
  },
  {
    id: 156,
    question: "A federal law that prohibits business practices that restrain trade.",
    answer: "Sherman Antitrust Act",
    category: "Practice of Real Estate",
    difficulty: "hard",
    explanation: "In real estate, this includes price-fixing, group boycotts, and market allocation."
  },
  {
    id: 157,
    question: "An illegal agreement between competing brokers to set standard commission rates.",
    answer: "Price-fixing",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Commissions must always be negotiable."
  },
  {
    id: 158,
    question: "An illegal agreement between brokers to not cooperate with another broker or firm.",
    answer: "Group boycott",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "This is done to reduce competition."
  },
  {
    id: 159,
    question: "An illegal agreement between brokers to divide their markets or territories.",
    answer: "Allocation of markets",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "This limits competition and is an antitrust violation."
  },
  {
    id: 160,
    question: "An illegal agreement to sell one product only if the buyer purchases another product as well.",
    answer: "Tie-in agreement",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Example: \"I'll only list your home if you use my mortgage company.\""
  },
  {
    id: 161,
    question: "A federal law prohibiting housing discrimination based on protected classes.",
    answer: "Fair Housing Act",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Protects against discrimination in the sale, rental, and financing of housing."
  },
  {
    id: 162,
    question: "What are the seven protected classes under the federal Fair Housing Act?",
    answer: "Race, Color, Religion, National Origin, Sex, Disability, and Familial Status.",
    category: "Practice of Real Estate",
    difficulty: "medium",
    explanation: "Remember FReSH CoRN: Familial Status, Race, Sex, Handicap, Color, Religion, National Origin."
  },
  {
    id: 163,
    question: "The illegal practice of inducing panic selling by claiming protected classes are moving into the area.",
    answer: "Blockbusting (Panic peddling)",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "This is also known as panic peddling."
  },
  {
    id: 164,
    question: "The illegal practice of channeling buyers toward or away from areas based on their protected class.",
    answer: "Steering",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "For example, only showing minority buyers homes in minority neighborhoods."
  },
  {
    id: 165,
    question: "The illegal practice by lenders of refusing to make loans in certain areas.",
    answer: "Redlining",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "This is often based on the racial or ethnic makeup of the area."
  },
  {
    id: 166,
    question: "A federal law requiring reasonable accommodations for people with disabilities in public places.",
    answer: "Americans with Disabilities Act (ADA)",
    category: "Practice of Real Estate",
    difficulty: "medium",
    explanation: "This applies to real estate offices and other commercial properties."
  },
  {
    id: 167,
    question: "A separate bank account a broker uses to hold other people's money.",
    answer: "Escrow account (Trust account)",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Used for funds like earnest money and security deposits."
  },
  {
    id: 168,
    question: "Illegally mixing trust funds with the broker's personal or business funds.",
    answer: "Commingling",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Trust funds must always be kept separate."
  },
  {
    id: 169,
    question: "The illegal use of trust funds for personal or business purposes.",
    answer: "Conversion",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "This is essentially theft of client funds."
  },
  {
    id: 170,
    question: "A fact important enough to influence a person's decision.",
    answer: "Material fact",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "Brokers must disclose all known adverse material facts to all parties."
  },
  {
    id: 171,
    question: "Are sellers and agents in Georgia required to disclose stigmatized property information (e.g., a death or crime occurred there)?",
    answer: "No. Georgia law states these are not material facts that must be disclosed.",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "However, an agent must answer truthfully if asked a direct question."
  },
  {
    id: 172,
    question: "A law requiring disclosure of known lead-based paint for properties built before 1978.",
    answer: "Residential Lead-Based Paint Hazard Reduction Act",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "Sellers and landlords must provide a specific pamphlet and disclosure form."
  },
  {
    id: 173,
    question: "A form where the seller details their knowledge of the property's condition.",
    answer: "Seller's property disclosure statement",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "While not required by Georgia law, it is a common practice to limit liability."
  },
  {
    id: 174,
    question: "The government's right to create laws to protect public health, safety, and welfare.",
    answer: "Police power",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "Zoning, building codes, and environmental laws are examples."
  },
  {
    id: 175,
    question: "Local laws that regulate and control the use of land.",
    answer: "Zoning ordinances",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "They divide areas into zones (residential, commercial) and specify permitted uses."
  },
  {
    id: 176,
    question: "A permit that allows a deviation from a zoning rule due to a hardship.",
    answer: "Variance",
    category: "Land Use Controls",
    difficulty: "medium",
    explanation: "For example, allowing a building to be closer to a property line than normally permitted."
  },
  {
    id: 177,
    question: "A property use that was legal but no longer conforms to current zoning.",
    answer: "Nonconforming use",
    category: "Land Use Controls",
    difficulty: "medium",
    explanation: "The use is often \"\"grandfathered in\"\" but cannot be expanded."
  },
  {
    id: 178,
    question: "Government regulations setting minimum standards for construction.",
    answer: "Building codes",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "They ensure safety in areas like structure, plumbing, and electrical systems."
  },
  {
    id: 179,
    question: "The reversion of property to the state when an owner dies without a will or heirs.",
    answer: "Escheat",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "This prevents property from becoming ownerless."
  },
  {
    id: 180,
    question: "Private rules that limit the use of property in a development.",
    answer: "Restrictive covenants (Deed restrictions)",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "They are created by a developer to maintain standards and are enforced by the HOA."
  },
  {
    id: 181,
    question: "A contract transferring the right of possession in exchange for rent.",
    answer: "Lease",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "It is both a contract and a conveyance of a leasehold estate."
  },
  {
    id: 182,
    question: "A lease where the tenant pays a fixed rent and the landlord pays all expenses.",
    answer: "Gross lease",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "This is common for residential leases."
  },
  {
    id: 183,
    question: "A lease where the tenant pays base rent plus some or all property expenses.",
    answer: "Net lease",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "Common for commercial leases. A triple-net (NNN) lease means the tenant pays all expenses."
  },
  {
    id: 184,
    question: "A lease where the tenant pays base rent plus a percentage of gross sales.",
    answer: "Percentage lease",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "Common for retail tenants in shopping centers."
  },
  {
    id: 185,
    question: "When a landlord's failure to act makes a property uninhabitable, forcing the tenant to leave.",
    answer: "Constructive eviction",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "This allows the tenant to terminate the lease. Example: no heat in winter."
  },
  {
    id: 186,
    question: "A person or company hired to manage real estate for an owner.",
    answer: "Property manager",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "They are typically a general agent for the owner."
  },
  {
    id: 187,
    question: "What is the primary goal of a property manager?",
    answer: "To maximize the owner's net operating income while preserving the property's value.",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "This involves balancing income, expenses, and maintenance."
  },
  {
    id: 188,
    question: "The six-member state body that regulates the real estate industry in Georgia.",
    answer: "Georgia Real Estate Commission (GREC)",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Its primary purpose is to protect the public."
  },
  {
    id: 189,
    question: "What is the primary purpose of the Georgia Real estate License Law?",
    answer: "To protect the public from dishonest or incompetent licensees.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "It establishes the requirements and standards of conduct for licensees."
  },
  {
    id: 190,
    question: "Who needs a real estate license in Georgia?",
    answer: "Anyone who performs real estate brokerage acts for another for a fee.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This includes selling, buying, renting, or managing property."
  },
  {
    id: 191,
    question: "What are the qualifications to become a real estate salesperson in Georgia?",
    answer: "18 years old, GA resident, high school diploma, 75-hour course, pass state exam.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Applicants must also pass a criminal background check."
  },
  {
    id: 192,
    question: "After passing the salesperson exam, how long do you have to apply for your license?",
    answer: "12 months.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "If you wait longer, your exam results expire."
  },
  {
    id: 193,
    question: "A 25-hour post-license course within the first year of licensure.",
    answer: "Post-license education requirement",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "Failure to complete this course will cause the license to lapse."
  },
  {
    id: 194,
    question: "What are the continuing education (CE) requirements for renewing a Georgia real estate license?",
    answer: "36 hours of CE every four years.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "At least 3 of these hours must be on license law."
  },
  {
    id: 195,
    question: "What happens if a licensee does not complete their required CE by the renewal deadline?",
    answer: "Their license will lapse.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Reinstatement requires paying fees and meeting specific education requirements."
  },
  {
    id: 196,
    question: "The one broker responsible for supervising a real estate firm and its agents.",
    answer: "Qualifying broker",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Every real estate firm in Georgia must have one."
  },
  {
    id: 197,
    question: "Can a Georgia broker hold more than one broker's license?",
    answer: "No, a person can only hold one broker license at a time.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "However, they can serve as the qualifying broker for multiple firms."
  },
  {
    id: 198,
    question: "The Brokerage Relationships in Real Estate Transactions Act.",
    answer: "BRRETA",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This is the Georgia law that governs agency relationships."
  },
  {
    id: 199,
    question: "According to BRRETA, what duties does a broker owe to all clients AND customers?",
    answer: "Present offers, account for funds, disclose adverse facts, advise expert help, and be honest.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "These are the baseline duties owed to everyone in a transaction."
  },
  {
    id: 200,
    question: "According to BRRETA, what additional duties does a broker owe only to a CLIENT?",
    answer: "Perform engagement terms, promote client's best interests, confidentiality, obey instructions, use reasonable care.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "These are the fiduciary duties that create a true agency relationship."
  },
  {
    id: 201,
    question: "Does Georgia law permit dual agency?",
    answer: "Yes, but only with the informed written consent of both parties.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This consent must be obtained before a written offer is made."
  },
  {
    id: 202,
    question: "What must a Georgia listing agreement contain?",
    answer: "It must be in writing with a definite expiration date and no automatic renewal.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "It must also clearly state the terms, including the commission."
  },
  {
    id: 203,
    question: "How soon must a broker deposit trust funds (like earnest money) into their trust account?",
    answer: "Promptly, which generally means within 3 business days.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This is a strict rule designed to protect client funds."
  },
  {
    id: 204,
    question: "Can a Georgia broker's trust account be interest-bearing?",
    answer: "Yes, if the contract specifies who gets the interest and all parties agree in writing.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "The broker cannot keep the interest unless it is agreed upon as compensation."
  },
  {
    id: 205,
    question: "A state fund to reimburse the public for monetary damages caused by licensees.",
    answer: "Real Estate Education, Research, and Recovery Fund",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "If the fund pays on behalf of a licensee, their license is automatically revoked."
  },
  {
    id: 206,
    question: "What is the maximum amount that can be paid from the Recovery Fund per transaction and per licensee?",
    answer: "$25,000 per transaction and $75,000 total per licensee.",
    category: "Georgia State Laws and Rules",
    difficulty: "hard",
    explanation: "This limits the fund's total liability."
  },
  {
    id: 207,
    question: "What is considered an \"\"unfair trade practice\"\" in Georgia?",
    answer: "Acts like misrepresentation, commingling, or failing to have an expiration date in a listing.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Violating these rules can lead to disciplinary action by GREC."
  },
  {
    id: 208,
    question: "What are the advertising rules for Georgia licensees?",
    answer: "All ads must be in the brokerage firm's name and cannot be misleading.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "The firm's name must be displayed prominently."
  },
  {
    id: 209,
    question: "When must a licensee who is buying or selling their own property disclose their licensed status?",
    answer: "They must disclose their status as a licensee in the contract.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This disclosure must be in writing."
  },
  {
    id: 210,
    question: "Georgia's state law prohibiting housing discrimination, which mirrors the federal act.",
    answer: "Georgia Fair Housing Law",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "It is enforced by the Georgia Commission on Equal Opportunity."
  },
  {
    id: 211,
    question: "In Georgia, what is the role of an attorney at closing?",
    answer: "A licensed attorney must conduct the closing and disburse all funds.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "Georgia is an \"attorney state.\""
  },
  {
    id: 212,
    question: "The legal instrument used in Georgia to secure a debt, instead of a mortgage.",
    answer: "Security deed",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "It conveys title to the lender until the debt is paid."
  },
  {
    id: 213,
    question: "What is the primary difference between a mortgage and a security deed?",
    answer: "A mortgage creates a lien; a security deed transfers title to the lender.",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "The security deed allows for a faster, non-judicial foreclosure process."
  },
  {
    id: 214,
    question: "What type of foreclosure process is most common in Georgia?",
    answer: "Non-judicial foreclosure.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This is allowed due to the \"\"power of sale\"\" clause in security deeds."
  },
  {
    id: 215,
    question: "What is the \"\"right of redemption\"\" in Georgia?",
    answer: "There is no statutory right of redemption after a foreclosure sale in Georgia.",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "The owner's rights are terminated at the time of the sale."
  },
  {
    id: 216,
    question: "A property tax system where taxes are based on the assessed value of the property.",
    answer: "Ad valorem tax system",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "Ad valorem means \"according to value.\""
  },
  {
    id: 217,
    question: "The tax rate used to calculate property taxes. 1 mill = $1 per $1,000 of assessed value.",
    answer: "Millage rate",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This rate is set by local government bodies."
  },
  {
    id: 218,
    question: "How is the assessed value of a property calculated in Georgia?",
    answer: "The assessed value is 40% of the fair market value.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "For a $200,000 property, the assessed value is $80,000."
  },
  {
    id: 219,
    question: "A tax exemption that reduces the property tax on a primary residence.",
    answer: "Homestead exemption",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "Homeowners must apply for this exemption with their county."
  },
  {
    id: 220,
    question: "A state law governing the creation, sale, and management of condominiums.",
    answer: "Georgia Condominium Act",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "It requires extensive disclosures for new condo unit buyers."
  },
  {
    id: 221,
    question: "A state law that regulates the sale of time-share interests.",
    answer: "Georgia Time-Share Act",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "It provides consumers with a 7-day right to cancel the contract."
  },
  {
    id: 222,
    question: "A homeowners' association (HOA) that makes and enforces rules for a development.",
    answer: "Community Association",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "Membership is typically mandatory for property owners."
  },
  {
    id: 223,
    question: "A licensed professional who manages the operations of an HOA.",
    answer: "Community Association Manager (CAM)",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "A CAM license is required to manage more than one association."
  },
  {
    id: 224,
    question: "A state law making it a felony to commit mortgage fraud.",
    answer: "Georgia Residential Mortgage Fraud Act",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "This includes lying on a loan application or participating in fraudulent schemes."
  },
  {
    id: 225,
    question: "A state tax on long-term real estate loans (over 3 years).",
    answer: "Intangibles tax",
    category: "Finance and Closing",
    difficulty: "medium",
    explanation: "The rate is $1.50 for each $500 (or fraction thereof) of the loan amount."
  },
  {
    id: 226,
    question: "A state tax on the transfer of real property, paid when the deed is recorded.",
    answer: "Transfer tax",
    category: "Finance and Closing",
    difficulty: "medium",
    explanation: "The rate is $0.10 for each $100 (or fraction thereof) of the sales price."
  },
  {
    id: 227,
    question: "Who typically pays the transfer tax and intangibles tax in Georgia?",
    answer: "The seller typically pays the transfer tax; the buyer pays the intangibles tax.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "These are customary but can be negotiated."
  },
  {
    id: 228,
    question: "How is the transfer tax calculated?",
    answer: "(Sales Price / 100) x $0.10. (Round price up to the nearest $100).",
    category: "Finance and Closing",
    difficulty: "hard",
    explanation: "It is a tax on the value being transferred."
  },
  {
    id: 229,
    question: "How is the intangibles tax calculated?",
    answer: "(Loan Amount / 500) x $1.50. (Round loan up to the nearest $500).",
    category: "Finance and Closing",
    difficulty: "hard",
    explanation: "It is a tax on the new long-term debt being recorded."
  },
  {
    id: 230,
    question: "A document filed to show a security deed has been paid in full.",
    answer: "Cancellation of Deed to Secure Debt",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "This is filed to show the debt has been satisfied."
  },
  {
    id: 231,
    question: "An act that does not require professional judgment or skill.",
    answer: "Ministerial act",
    category: "Agency",
    difficulty: "medium",
    explanation: "These can be performed for a customer without creating an agency relationship."
  },
  {
    id: 232,
    question: "What is the statute of limitations in Georgia for a written contract?",
    answer: "6 years.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "A lawsuit for breach of a written contract must be filed within this period."
  },
  {
    id: 233,
    question: "What is Georgia's law regarding adverse possession?",
    answer: "20 years of possession, or 7 years with",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "\"color of title.\"\".\",Color of title means possession is based on a seemingly valid but defective document."
  },
  {
    id: 234,
    question: "A property is assessed at 40% of market value. Market value is $300,000 and the millage is 25 mills. What are the annual taxes?",
    answer: "$3,000",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Property taxes are calculated on assessed value (not market value) using the millage rate. A mill is $0.001 per dollar of assessed value, or $1 per $1,000. Step 1: Calculate assessed value: $300,000 × 0.40 = $120,000. Step 2: Convert 25 mills to a decimal: 25 mills = 25 × $0.001 = $0.025 per dollar, or 25 ÷ 1,000 = 0.025. Step 3: Multiply assessed value by millage rate: $120,000 × 0.025 = $3,000. Alternatively: ($120,000 ÷ $1,000) × 25 = $3,000. The millage rate is set by local taxing authorities (county, city, school district)."
  },
  {
    id: 235,
    question: "Can a Georgia salesperson or associate broker accept a commission directly from a client?",
    answer: "No, all commissions must be paid through the licensee's broker.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "The broker is responsible for paying their affiliated licensees."
  },
  {
    id: 236,
    question: "What type of brokerage engagement does not require a termination date under Georgia law?",
    answer: "A buyer brokerage agreement.",
    category: "Georgia State Laws and Rules",
    difficulty: "hard",
    explanation: "These agreements can terminate upon completion of the task (finding a home)."
  },
  {
    id: 237,
    question: "A penalty tax on property that is converted from a special use (like agriculture).",
    answer: "Roll-back tax",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "It recaptures the tax savings the owner received under the special assessment."
  },
  {
    id: 238,
    question: "On a closing disclosure, how would the earnest money be shown?",
    answer: "As a credit to the buyer.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It is money the buyer has already paid toward the purchase."
  },
  {
    id: 239,
    question: "Can an agent disclose the terms of one buyer's offer to another competing buyer?",
    answer: "Only with the express written permission of the first buyer.",
    category: "Agency",
    difficulty: "medium",
    explanation: "Disclosing without permission violates the duty of confidentiality."
  },
  {
    id: 240,
    question: "The primary purpose is to show the buyer's good faith and intent to purchase.",
    answer: "Earnest money",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It is not a legal requirement for a valid contract but is standard practice."
  },
  {
    id: 241,
    question: "Charging an interest rate higher than the legal maximum.",
    answer: "Usury",
    category: "Financing",
    difficulty: "medium",
    explanation: "Georgia has usury laws, but many first mortgages are exempt."
  },
  {
    id: 242,
    question: "The rights of a landowner whose property borders a flowing body of water (river, stream).",
    answer: "Riparian rights",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "In GA, ownership often extends to the center of a non-navigable waterway."
  },
  {
    id: 243,
    question: "The increase in value from combining two or more adjacent lots.",
    answer: "Plottage",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "The process of combining them is called assemblage."
  },
  {
    id: 244,
    question: "The act of filing a deed with the county's Clerk of Superior Court.",
    answer: "Recording",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Recording provides constructive notice to the public."
  },
  {
    id: 245,
    question: "Can a real estate licensee in Georgia draft legal documents like contracts or deeds?",
    answer: "No, licensees can only fill in the blanks on attorney-approved forms.",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Drafting legal language is the unauthorized practice of law."
  },
  {
    id: 246,
    question: "Under Georgia's Stigmatized Property Statute, does an agent have to disclose if a prior occupant had HIV or AIDS?",
    answer: "No, this is specifically protected information and cannot be disclosed.",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "This is to prevent discrimination."
  },
  {
    id: 247,
    question: "A strip of land separating one land use from another.",
    answer: "Buffer zone",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "For example, a park between a residential and a commercial zone."
  },
  {
    id: 248,
    question: "In Georgia, what is the maximum security deposit a landlord can require?",
    answer: "There is no statutory limit on the amount.",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "However, the landlord must follow strict rules for holding and returning the deposit."
  },
  {
    id: 249,
    question: "If a broker's license is suspended or revoked by GREC, what happens to the licenses of the affiliated agents?",
    answer: "Their licenses are automatically inactivated.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "They must transfer to a new broker to resume brokerage activities."
  },
  {
    id: 250,
    question: "The standard sales contract form widely used by real estate agents in Georgia.",
    answer: "GAR Purchase and Sale Agreement",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "While not legally required, these forms are widely used."
  },
  {
    id: 251,
    question: "The division of expenses between the buyer and seller.",
    answer: "Proration",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "This ensures each party pays for the time they owned the property."
  },
  {
    id: 252,
    question: "Any personal, familial, or business relationship that could affect a broker's objectivity.",
    answer: "Material relationship",
    category: "Agency",
    difficulty: "medium",
    explanation: "For example, if the agent is related to the other party."
  },
  {
    id: 253,
    question: "A clause stating that certain contract obligations continue to be enforceable even after closing.",
    answer: "Survival clause",
    category: "Contracts",
    difficulty: "hard",
    explanation: "For example, a seller's warranty about the property's condition might survive."
  },
  {
    id: 254,
    question: "When a buyer takes over the seller's existing mortgage.",
    answer: "Assumption of a mortgage",
    category: "Financing",
    difficulty: "easy",
    explanation: "This is uncommon today due to \"\"due-on-sale\"\" clauses."
  },
  {
    id: 255,
    question: "The right to use and control the space above a parcel of land.",
    answer: "Air rights",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "These rights can be sold or leased separately."
  },
  {
    id: 256,
    question: "The principle that a superior property's value is negatively affected by surrounding inferior properties.",
    answer: "Regression",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "An expensive home in a modest neighborhood will be worth less."
  },
  {
    id: 257,
    question: "A person who has made a valid will.",
    answer: "Testator",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "This is the person who has died."
  },
  {
    id: 258,
    question: "What is the difference between \"\"ethics\"\" and \"\"laws\"\"?",
    answer: "Laws are legally enforceable rules; ethics are moral principles and standards.",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "The REALTOR® Code of Ethics often imposes a higher standard than the law."
  },
  {
    id: 259,
    question: "Does Georgia have a specific mandatory seller property disclosure form required by state law?",
    answer: "No, Georgia does not have a state-mandated disclosure form.",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "However, using a form (like the GAR form) is a best practice to avoid liability."
  },
  {
    id: 260,
    question: "The legal process a landlord must use to remove a tenant.",
    answer: "Dispossessory action (Eviction)",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "Landlords cannot use \"\"self-help\"\" methods like changing the locks."
  },
  {
    id: 261,
    question: "Insurance required for brokers who manage community association funds.",
    answer: "Fidelity Bond (Fidelity Insurance)",
    category: "Georgia State Laws and Rules",
    difficulty: "hard",
    explanation: "It protects the association from theft or fraud by the broker."
  },
  {
    id: 262,
    question: "In Georgia, for how long must a broker keep copies of real estate transaction documents?",
    answer: "A minimum of 3 years.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This includes contracts, closing statements, and brokerage agreements."
  },
  {
    id: 263,
    question: "A law requiring funds over $5,000 for closing to be immediately available (wire, certified check).",
    answer: "Good funds law",
    category: "Finance and Closing",
    difficulty: "medium",
    explanation: "This ensures all parties can be paid at closing without delay."
  },
  {
    id: 264,
    question: "A legal doctrine holding a principal (broker) responsible for their agent's actions.",
    answer: "Vicarious liability",
    category: "Agency",
    difficulty: "medium",
    explanation: "This is why broker supervision is critical."
  },
  {
    id: 265,
    question: "An addition to a contract, attached as a separate document.",
    answer: "Addendum",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Example: a financing addendum that details the loan terms."
  },
  {
    id: 266,
    question: "A clause requiring the loan to be paid in full if the property is sold.",
    answer: "Due-on-sale clause (Alienation clause)",
    category: "Financing",
    difficulty: "easy",
    explanation: "This prevents loan assumptions without the lender's consent."
  },
  {
    id: 267,
    question: "The rights of a landowner whose property borders a large, navigable body of water (lake, ocean).",
    answer: "Littoral rights",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "These rights typically extend to the high-water mark."
  },
  {
    id: 268,
    question: "The principle that an inferior property's value is enhanced by surrounding superior properties.",
    answer: "Progression",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "A modest home in an expensive neighborhood will be worth more."
  },
  {
    id: 269,
    question: "The court process of validating a will and settling an estate.",
    answer: "Probate",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Probate ensures assets are distributed correctly and debts are paid."
  },
  {
    id: 270,
    question: "The largest trade association for real estate professionals in the U.S.",
    answer: "National Association of REALTORS® (NAR)",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Members are called REALTORS® and must abide by a Code of Ethics."
  },
  {
    id: 271,
    question: "A colorless, odorless, radioactive gas from the natural decay of uranium.",
    answer: "Radon",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "It can seep into homes and is a known health hazard. The EPA recommends testing."
  },
  {
    id: 272,
    question: "A local government's long-term plan for community growth and development.",
    answer: "Comprehensive plan (Master plan)",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "Zoning is the tool used to implement this plan."
  },
  {
    id: 273,
    question: "The transfer of a tenant's entire leasehold interest to another person.",
    answer: "Assignment of a lease",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "The new tenant (assignee) becomes primarily responsible for the rent."
  },
  {
    id: 274,
    question: "A listing where the broker's commission is any amount above a specified net price for the seller. It is illegal in Georgia.",
    answer: "Net listing",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This is illegal because it creates a conflict of interest."
  },
  {
    id: 275,
    question: "A legal rule preventing property from being controlled for an unreasonably long time after an owner's death.",
    answer: "Rule Against Perpetuities",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "It ensures property can be freely transferred by future generations."
  },
  {
    id: 276,
    question: "An old closing form, now replaced by the Closing Disclosure for most transactions.",
    answer: "HUD-1 Settlement Statement",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It itemized all charges for the buyer and seller."
  },
  {
    id: 277,
    question: "A form of dual agency where the broker appoints separate agents for the buyer and seller.",
    answer: "Designated agency",
    category: "Agency",
    difficulty: "easy",
    explanation: "This provides more dedicated representation than traditional dual agency."
  },
  {
    id: 278,
    question: "A rule stating that a written contract overrides any prior or contemporaneous oral agreements.",
    answer: "Parol evidence rule",
    category: "Contracts",
    difficulty: "hard",
    explanation: "This emphasizes the importance of getting all terms in writing."
  },
  {
    id: 279,
    question: "A loan paid off in regular installments, so the balance is zero at the end of the term.",
    answer: "Amortized loan",
    category: "Financing",
    difficulty: "easy",
    explanation: "Most home loans are fully amortized."
  },
  {
    id: 280,
    question: "The degree, quantity, and nature of a person's interest in real property.",
    answer: "Estate",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "The type of estate defines the owner's rights and the duration of ownership."
  },
  {
    id: 281,
    question: "The final step where an appraiser weighs the values from the different approaches to arrive at a final estimate.",
    answer: "Reconciliation",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "It is not simply an average."
  },
  {
    id: 282,
    question: "To die without a valid will.",
    answer: "Intestate",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "The property is then distributed according to the state's laws of descent."
  },
  {
    id: 283,
    question: "What does the term \"\"REALTOR®\"\" signify?",
    answer: "That the licensee is a member of the National Association of REALTORS® (NAR).",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "This membership requires adherence to a strict Code of Ethics."
  },
  {
    id: 284,
    question: "A mineral fiber once used in building materials that can cause lung disease if inhaled.",
    answer: "Asbestos",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "Its presence is a material fact that must be disclosed if known."
  },
  {
    id: 285,
    question: "Covenants, Conditions, and Restrictions. Private rules that govern a subdivision.",
    answer: "CC&Rs",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "They are created by the developer and enforced by the HOA."
  },
  {
    id: 286,
    question: "When a tenant transfers part of their leasehold interest to another, but retains some responsibility.",
    answer: "Sublease",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "The original tenant is still primarily liable to the landlord."
  },
  {
    id: 287,
    question: "Who appoints the members of the Georgia Real Estate Commission (GREC)?",
    answer: "The Governor of Georgia.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "The members are then confirmed by the state senate."
  },
  {
    id: 288,
    question: "A map of a subdivision showing the locations and boundaries of individual lots.",
    answer: "Plat map",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "It is recorded in the public records."
  },
  {
    id: 289,
    question: "A charge; an amount a party owes and must pay.",
    answer: "Debit",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The purchase price is a debit to the buyer."
  },
  {
    id: 290,
    question: "An amount a party is receiving or has already paid.",
    answer: "Credit",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The loan amount is a credit to the buyer."
  },
  {
    id: 291,
    question: "What is the difference between a client and a customer in Georgia under BRRETA?",
    answer: "A client has a written agency contract; a customer does not.",
    category: "Agency",
    difficulty: "easy",
    explanation: "An agent performs ministerial acts for a customer and owes fiduciary duties to a client."
  },
  {
    id: 292,
    question: "A rejection of an offer combined with a new offer.",
    answer: "Counteroffer",
    category: "Contracts",
    difficulty: "easy",
    explanation: "The original offer is terminated by the counteroffer."
  },
  {
    id: 293,
    question: "The market where loans are originated directly to borrowers.",
    answer: "Primary mortgage market",
    category: "Financing",
    difficulty: "easy",
    explanation: "This includes banks, credit unions, and mortgage companies."
  },
  {
    id: 294,
    question: "An ownership interest that gives the right to use a property for a specified period each year.",
    answer: "Time-share",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "Commonly used for vacation properties."
  },
  {
    id: 295,
    question: "A transaction between two unrelated, well-informed parties acting in their own self-interest.",
    answer: "Arm's length transaction",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "Appraisals assume an arm's length transaction to determine market value."
  },
  {
    id: 296,
    question: "A will written entirely in the testator's handwriting. Not recognized in Georgia.",
    answer: "Holographic will",
    category: "Transfer of Title",
    difficulty: "medium",
    explanation: "A valid Georgia will must be signed and witnessed."
  },
  {
    id: 297,
    question: "Errors and Omissions insurance. A form of professional liability insurance.",
    answer: "E&O insurance",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "It protects licensees from claims of mistakes or negligence."
  },
  {
    id: 298,
    question: "Tanks used to store petroleum or hazardous materials, which can leak and contaminate soil.",
    answer: "Underground storage tanks (USTs)",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "The presence of USTs is a material fact and an environmental concern."
  },
  {
    id: 299,
    question: "Government permission to construct or renovate a building.",
    answer: "Building permit",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "It certifies that the plans comply with building codes."
  },
  {
    id: 300,
    question: "In Georgia, how must a landlord handle a tenant's security deposit?",
    answer: "The landlord must place it in a separate escrow account and notify the tenant.",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "The landlord must also provide a move-in inspection list."
  },
  {
    id: 301,
    question: "What activities can an unlicensed assistant legally perform in Georgia?",
    answer: "Purely administrative tasks, such as answering phones and typing contracts.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "They cannot show property, negotiate, or perform any activity requiring a license."
  },
  {
    id: 302,
    question: "A state law that provides a framework and certain powers for HOAs.",
    answer: "Georgia Property Owners' Association Act (POAA)",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "A development must explicitly opt-in to be governed by the POAA."
  },
  {
    id: 303,
    question: "When must the \"\"Closing Disclosure\"\" be provided to the buyer/borrower?",
    answer: "At least 3 business days before the closing.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "This TRID rule gives the buyer time to review the final loan terms and costs."
  },
  {
    id: 304,
    question: "When does a brokerage engagement (agency agreement) need to be in writing in Georgia?",
    answer: "All exclusive brokerage engagements must be in writing.",
    category: "Agency",
    difficulty: "easy",
    explanation: "This includes exclusive right-to-sell and exclusive buyer brokerage agreements."
  },
  {
    id: 305,
    question: "A court order forcing a party to complete their performance of a contract.",
    answer: "Specific performance",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A common remedy for a buyer when a seller breaches a sales contract."
  },
  {
    id: 306,
    question: "The difference between a property's market value and the amount owed on it.",
    answer: "Equity",
    category: "Financing",
    difficulty: "easy",
    explanation: "Equity = Market Value - Loan Balance."
  },
  {
    id: 307,
    question: "Historical rights of a spouse to property of a deceased spouse. Abolished in Georgia.",
    answer: "Dower and curtesy",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Georgia protects surviving spouses with inheritance laws and \"year's support.\""
  },
  {
    id: 308,
    question: "In the sales comparison approach, what happens if a comparable property is superior to the subject property?",
    answer: "Subtract value from the comparable's sales price.",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "If the Comp is Better, you Subtract (CBS)."
  },
  {
    id: 309,
    question: "In the sales comparison approach, what happens if a comparable property is inferior to the subject property?",
    answer: "Add value to the comparable's sales price.",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "If the Comp is Inferior, you Add (CIA)."
  },
  {
    id: 310,
    question: "Receiving title to real property through a will.",
    answer: "Title by devise",
    category: "Transfer of Title",
    difficulty: "medium",
    explanation: "The person who receives the property is called the devisee."
  },
  {
    id: 311,
    question: "What is the difference between a \"\"salesperson\"\" and a \"\"broker\"\" in Georgia?",
    answer: "A broker can operate independently; a salesperson must be sponsored by a broker.",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "A salesperson's license must be held by a qualifying broker."
  },
  {
    id: 312,
    question: "Under BRRETA, an agent must disclose adverse physical conditions they know or...",
    answer: "...could have known within the scope of their expertise.",
    category: "Property Disclosures",
    difficulty: "hard",
    explanation: "This creates a standard of reasonable professional competence."
  },
  {
    id: 313,
    question: "A lease with pre-scheduled rent increases at specified future dates.",
    answer: "Graduated lease",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "The increases can be a fixed amount or tied to an index."
  },
  {
    id: 314,
    question: "Can a Georgia licensee offer a lottery or contest to induce someone to buy or sell real estate?",
    answer: "No, this is an illegal unfair trade practice in Georgia.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "It is considered a deceptive marketing practice."
  },
  {
    id: 315,
    question: "A method of describing land by its boundaries, using distances and directions from a point of beginning.",
    answer: "Metes and bounds system",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "It is one of the oldest and most precise forms of legal description."
  },
  {
    id: 316,
    question: "A document recorded to show that a loan has been paid in full.",
    answer: "Satisfaction of mortgage (Cancellation of security deed)",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "This clears the lien from the public record."
  },
  {
    id: 317,
    question: "An illegal situation where an agent represents both parties without their informed written consent.",
    answer: "Undisclosed dual agency",
    category: "Agency",
    difficulty: "easy",
    explanation: "This is a serious violation of license law."
  },
  {
    id: 318,
    question: "A unilateral contract giving a buyer the right to purchase at a set price and term.",
    answer: "Option contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "The buyer is not obligated to buy, but the seller is obligated to sell."
  },
  {
    id: 319,
    question: "A loan that includes both real and personal property as collateral.",
    answer: "Package mortgage",
    category: "Financing",
    difficulty: "medium",
    explanation: "Example: a loan for a furnished condo that includes the furniture."
  },
  {
    id: 320,
    question: "A personal, revocable privilege to use another's land for a specific purpose.",
    answer: "License",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "It is not an interest in the land and can be revoked at any time."
  },
  {
    id: 321,
    question: "The process of combining two or more adjoining lots into one larger tract.",
    answer: "Assemblage",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "The resulting increase in value is called plottage."
  },
  {
    id: 322,
    question: "The reversion of property to the state when an owner dies with no will and no heirs.",
    answer: "Escheat",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Escheat prevents property from becoming ownerless."
  },
  {
    id: 323,
    question: "An advertisement that does not identify the brokerage firm. It is illegal in Georgia.",
    answer: "Blind ad",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "All ads must include the registered name of the brokerage."
  },
  {
    id: 324,
    question: "The Comprehensive Environmental Response, Compensation, and Liability Act.",
    answer: "CERCLA",
    category: "Property Disclosures",
    difficulty: "hard",
    explanation: "The federal \"\"Superfund\"\" law that establishes liability for cleaning up hazardous waste sites."
  },
  {
    id: 325,
    question: "The legal process of removing a tenant from a property for breaching the lease.",
    answer: "Eviction (Dispossessory proceeding)",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "In Georgia, this is called a dispossessory proceeding."
  },
  {
    id: 326,
    question: "For how long is a Georgia real estate license issued?",
    answer: "Four years.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Licenses are renewed every four years on the last day of the licensee's birth month."
  },
  {
    id: 327,
    question: "The right to pass across the land of another; a type of easement.",
    answer: "Right of way",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "Used for public roads, utility lines, and shared driveways."
  },
  {
    id: 328,
    question: "What is the difference between a \"\"pre-qualification\"\" and a \"\"pre-approval\"\" for a loan?",
    answer: "Pre-qualification is an informal estimate; pre-approval is a conditional commitment from a lender.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "Pre-approval is much stronger because the borrower's information has been verified."
  },
  {
    id: 329,
    question: "Exaggerated opinion-based statements about a property. Legal sales talk.",
    answer: "Puffing",
    category: "Agency",
    difficulty: "easy",
    explanation: "Example: \"\"This home has the best view in town!\"\" Not a statement of fact."
  },
  {
    id: 330,
    question: "Something of legal value given in exchange for a promise.",
    answer: "Consideration",
    category: "Contracts",
    difficulty: "easy",
    explanation: "It is an essential element for a contract to be valid."
  },
  {
    id: 331,
    question: "A final loan payment that is much larger than the regular monthly payments.",
    answer: "Balloon payment",
    category: "Financing",
    difficulty: "easy",
    explanation: "It pays off the remaining balance of a partially amortized loan."
  },
  {
    id: 332,
    question: "Fixtures installed by a commercial tenant for their business.",
    answer: "Trade fixtures",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "They are the tenant's personal property and can be removed before the lease ends."
  },
  {
    id: 333,
    question: "A loss of value from outdated design or features.",
    answer: "Functional obsolescence",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Example: a 4-bedroom house with only one bathroom."
  },
  {
    id: 334,
    question: "A written supplement or amendment to an existing will.",
    answer: "Codicil",
    category: "Transfer of Title",
    difficulty: "medium",
    explanation: "It must be executed with the same formalities as a will."
  },
  {
    id: 335,
    question: "A person hired to do a job who controls how the work is done.",
    answer: "Independent contractor",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Most real estate agents are independent contractors, not employees."
  },
  {
    id: 336,
    question: "Land that is subject to flooding.",
    answer: "Floodplain",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "Being in a floodplain is a material fact that must be disclosed."
  },
  {
    id: 337,
    question: "A tenant's right to possess property for the term of a lease.",
    answer: "Leasehold estate",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "It is considered the tenant's personal property."
  },
  {
    id: 338,
    question: "Can a Georgia licensee pay a referral fee to an unlicensed person?",
    answer: "No, a licensee can only pay a referral fee to another licensed real estate broker.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Paying for referrals from unlicensed persons is illegal."
  },
  {
    id: 339,
    question: "In Georgia, who is responsible for providing a termite letter (or official wood infestation report) at closing?",
    answer: "This is negotiable in the sales contract.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "It is not required by law, but most lenders in Georgia require it."
  },
  {
    id: 340,
    question: "A form that summarizes the key features, costs, and risks of a mortgage loan.",
    answer: "Loan Estimate",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It must be provided within 3 business days of application to help consumers shop for loans."
  },
  {
    id: 341,
    question: "A licensee who assists parties without representing them as an agent.",
    answer: "Facilitator (Transaction broker)",
    category: "Agency",
    difficulty: "easy",
    explanation: "They are a neutral third party and owe no fiduciary duties."
  },
  {
    id: 342,
    question: "A contract where performance has not yet been completed.",
    answer: "Executory contract",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A sales contract is executory until closing."
  },
  {
    id: 343,
    question: "Fees a lender charges to process a loan application.",
    answer: "Origination fees",
    category: "Financing",
    difficulty: "easy",
    explanation: "These are typically a percentage of the loan amount."
  },
  {
    id: 344,
    question: "An unauthorized physical intrusion of one person's property onto another's.",
    answer: "Encroachment",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "Example: a neighbor's fence being a foot over your property line."
  },
  {
    id: 345,
    question: "A loss of value from factors outside the property's boundaries.",
    answer: "External obsolescence",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Example: being located next to a noisy factory. It is considered incurable."
  },
  {
    id: 346,
    question: "Acquiring title by possessing property for a statutory period in an open, hostile way.",
    answer: "Adverse possession",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "The period in Georgia is 20 years (or 7 with color of title)."
  },
  {
    id: 347,
    question: "A fact that is significant enough to influence a party's decision.",
    answer: "Material fact",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Licensees must disclose known adverse material facts."
  },
  {
    id: 348,
    question: "A fungus that can grow in damp conditions and cause health issues.",
    answer: "Mold",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "The presence of significant mold is a material fact to be disclosed."
  },
  {
    id: 349,
    question: "A leasehold that can be terminated at any time by either party.",
    answer: "Estate at will",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "In Georgia, the landlord must give 60 days' notice; the tenant must give 30."
  },
  {
    id: 350,
    question: "What is the primary responsibility of a qualifying broker in Georgia?",
    answer: "To supervise the brokerage activities of all affiliated licensees.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "They are also responsible for the firm's trust accounts."
  },
  {
    id: 351,
    question: "The instrument used in Georgia instead of a mortgage to secure a loan.",
    answer: "Security deed",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "It conveys title to the lender as security."
  },
  {
    id: 352,
    question: "To divide expenses between the buyer and seller at closing.",
    answer: "Prorate",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "This ensures each party pays their fair share for their period of ownership."
  },
  {
    id: 353,
    question: "A written contract between a broker and a client establishing an agency relationship.",
    answer: "Brokerage engagement",
    category: "Agency",
    difficulty: "easy",
    explanation: "Examples include listing agreements and buyer brokerage agreements."
  },
  {
    id: 354,
    question: "A state law requiring real estate contracts to be in writing to be enforceable.",
    answer: "Statute of Frauds",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This prevents disputes over oral agreements."
  },
  {
    id: 355,
    question: "Principal, Interest, Taxes, and Insurance.",
    answer: "PITI",
    category: "Financing",
    difficulty: "easy",
    explanation: "The four components of a monthly mortgage payment."
  },
  {
    id: 356,
    question: "Co-ownership with an undivided interest and no right of survivorship.",
    answer: "Tenancy in common",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "A deceased owner's share goes to their heirs."
  },
  {
    id: 357,
    question: "A method of valuation based on the cost of land plus replacement cost of improvements, minus depreciation.",
    answer: "Cost approach",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Best for new construction or unique properties like schools."
  },
  {
    id: 358,
    question: "A deed providing the greatest protection to the buyer, warranting title for the property's entire history.",
    answer: "General warranty deed",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "This is the most common and desirable type of deed for buyers."
  },
  {
    id: 359,
    question: "The illegal practice of guiding buyers to or from areas based on their protected class.",
    answer: "Steering",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "A violation of the Fair Housing Act."
  },
  {
    id: 360,
    question: "Proximity to airport noise zones or accident potential zones should be disclosed.",
    answer: "Military air base disclosure",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "This is considered a potentially adverse material fact."
  },
  {
    id: 361,
    question: "When a landlord's negligence makes a property uninhabitable, forcing the tenant to leave.",
    answer: "Constructive eviction",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "The tenant can terminate the lease without penalty."
  },
  {
    id: 362,
    question: "If a licensee's application is denied by GREC, what recourse do they have?",
    answer: "The applicant has the right to a formal hearing to appeal the decision.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "The hearing must be requested within 60 days of the denial."
  },
  {
    id: 363,
    question: "What is the difference between a \"\"condominium\"\" and a \"\"townhouse\"\" in terms of ownership?",
    answer: "In a condo, you own the unit's airspace; in a townhouse, you own the unit and the land beneath it.",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "Townhouse ownership is more like owning a single-family home."
  },
  {
    id: 364,
    question: "A state tax on the sale of real property, typically paid by the seller.",
    answer: "Transfer tax",
    category: "Finance and Closing",
    difficulty: "medium",
    explanation: "The rate is $0.10 for every $100 of the sales price (after the first $1,000)."
  },
  {
    id: 365,
    question: "An agent appointed by a broker to represent a specific client in an in-house transaction.",
    answer: "Designated agent",
    category: "Agency",
    difficulty: "easy",
    explanation: "This provides individual representation and avoids dual agency for the agent."
  },
  {
    id: 366,
    question: "A condition in a contract that must be met for it to be binding.",
    answer: "Contingency",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Common examples are financing, inspection, and appraisal contingencies."
  },
  {
    id: 367,
    question: "A loan for homeowners 62+ that converts home equity into cash.",
    answer: "Reverse mortgage",
    category: "Financing",
    difficulty: "easy",
    explanation: "The loan is repaid when the home is sold or the owner dies."
  },
  {
    id: 368,
    question: "Ownership of property by one person or entity alone.",
    answer: "Severalty ownership",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "Also known as sole ownership."
  },
  {
    id: 369,
    question: "The most probable price a property should sell for in an open and competitive market.",
    answer: "Market value",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "The goal of an appraisal is to determine market value."
  },
  {
    id: 370,
    question: "A deed that transfers any interest the grantor may have, with no warranties.",
    answer: "Quitclaim deed",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Offers the least protection; often used to clear title defects."
  },
  {
    id: 371,
    question: "Illegally inducing panic selling by making claims about protected classes moving into an area.",
    answer: "Blockbusting",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "A violation of the Fair Housing Act."
  },
  {
    id: 372,
    question: "A form where the seller details their knowledge of the property's condition.",
    answer: "Seller's disclosure statement",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "Not required by GA law, but a common and recommended practice."
  },
  {
    id: 373,
    question: "A leasehold for a specific, fixed period with a definite start and end date.",
    answer: "Estate for years",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "It does not automatically renew."
  },
  {
    id: 374,
    question: "How many members are on the Georgia Real Estate Commission (GREC)?",
    answer: "Six members.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Five are licensed professionals, and one is an unlicensed member of the public."
  },
  {
    id: 375,
    question: "Taxes based on the assessed value of a property.",
    answer: "Ad valorem taxes",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This is the system for property taxes in Georgia."
  },
  {
    id: 376,
    question: "A state tax on long-term mortgage loans, paid by the borrower.",
    answer: "Intangibles tax",
    category: "Finance and Closing",
    difficulty: "medium",
    explanation: "The rate is $1.50 for every $500 of the loan amount."
  },
  {
    id: 377,
    question: "What is the difference between a \"\"Single Agent\"\" and a \"\"Transaction Broker\"\"?",
    answer: "A single agent is a fiduciary for one party; a transaction broker is a neutral facilitator for all parties.",
    category: "Agency",
    difficulty: "easy",
    explanation: "A single agent is an advocate; a transaction broker is not."
  },
  {
    id: 378,
    question: "A clause that means all deadlines in the contract are strict and must be met exactly.",
    answer: "Time is of the essence",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Failure to meet a deadline is a breach of contract."
  },
  {
    id: 379,
    question: "The marketplace where existing mortgage loans are bought and sold.",
    answer: "Secondary market",
    category: "Financing",
    difficulty: "easy",
    explanation: "It provides liquidity to primary lenders like banks."
  },
  {
    id: 380,
    question: "An interest in property that lasts for the duration of a person's life.",
    answer: "Life estate",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "The owner of a life estate is called a life tenant."
  },
  {
    id: 381,
    question: "The principle that value is maximized when a property conforms to its surroundings.",
    answer: "Principle of conformity",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "A home that is too big or too small for its neighborhood may lose value."
  },
  {
    id: 382,
    question: "Notice given to the public by recording a document.",
    answer: "Constructive notice",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "Recording a deed provides constructive notice of the transfer."
  },
  {
    id: 383,
    question: "Illegally refusing to make loans in certain neighborhoods, often based on race.",
    answer: "Redlining",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "A violation of the Fair Housing Act."
  },
  {
    id: 384,
    question: "Under Georgia law, does a seller or agent have to disclose if a property was the site of a homicide or other felony?",
    answer: "No, this is not considered a material fact that must be disclosed.",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "However, an agent cannot lie if asked a direct question."
  },
  {
    id: 385,
    question: "A lease where the tenant pays base rent plus operating expenses (taxes, insurance, etc.).",
    answer: "Net lease",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "Common in commercial real estate."
  },
  {
    id: 386,
    question: "Illegally mixing trust funds with a broker's personal or business funds.",
    answer: "Commingling",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "Trust funds must always be kept in a separate trust account."
  },
  {
    id: 387,
    question: "The tax rate for ad valorem taxes. One mill = $1 per $1,000 of assessed value.",
    answer: "Millage rate",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This rate is set by local taxing authorities (e.g., county, city, school board)."
  },
  {
    id: 388,
    question: "The five-page form detailing the final terms of a mortgage loan.",
    answer: "Closing Disclosure",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It must be given to the borrower at least 3 business days before closing."
  },
  {
    id: 389,
    question: "A property sold for $320,000, which was 95% of the listing price. What was the listing price?",
    answer: "$336,842.11",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "This is a reverse percentage calculation. If the sale price is 95% of the listing price, then the listing price is the sale price divided by 0.95. Step 1: Convert 95% to a decimal: 0.95. Step 2: Divide the sale price by the percentage (as a decimal): $320,000 ÷ 0.95 = $336,842.11. To verify: $336,842.11 × 0.95 = $320,000. This type of calculation is useful when you know the final amount and need to find the original amount before a percentage reduction or increase."
  },
  {
    id: 390,
    question: "A rectangular office space is 40 feet by 60 feet. Annual rent is $18 per sq ft. What is the monthly rent?",
    answer: "$3,600",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "Step 1: Calculate the area in square feet: 40 feet × 60 feet = 2,400 square feet. Step 2: Calculate annual rent: 2,400 sq ft × $18 per sq ft = $43,200 per year. Step 3: Convert annual rent to monthly rent: $43,200 ÷ 12 months = $3,600 per month. Commercial leases often quote rent per square foot per year, so you must calculate the total annual rent first, then divide by 12 to get the monthly amount."
  },
  {
    id: 391,
    question: "A seller wants to net $200,000 after paying a 6% commission. What is the minimum selling price?",
    answer: "$212,765.96",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "This is a reverse commission calculation. If the seller nets $200,000 after a 6% commission, the commission comes out of the sale price, so the seller keeps 94% (100% - 6%). Step 1: Calculate what percentage the seller keeps: 100% - 6% = 94%, or 0.94 as a decimal. Step 2: Divide the desired net amount by the percentage kept: $200,000 ÷ 0.94 = $212,765.96. To verify: $212,765.96 × 0.06 = $12,765.96 commission, leaving $212,765.96 - $12,765.96 = $200,000 net. Formula: Minimum Sale Price = Desired Net ÷ (1 - Commission Rate)."
  },
  {
    id: 392,
    question: "A loan of $180,000 has a monthly P&I payment of $1,050. The annual interest rate is 6%. How much of the first month's payment is interest?",
    answer: "$900",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "On an amortized loan, early payments consist mostly of interest. To find the first month's interest, calculate the annual interest and divide by 12. Step 1: Calculate annual interest: $180,000 × 0.06 (6% as decimal) = $10,800 per year. Step 2: Calculate monthly interest: $10,800 ÷ 12 = $900. The monthly P&I payment of $1,050 is not needed for this calculation - only the loan balance and interest rate matter. In the first month, $900 goes to interest and $150 goes to principal ($1,050 - $900 = $150). As the loan is paid down, the interest portion decreases and the principal portion increases."
  },
  {
    id: 393,
    question: "A property's market value is $400,000. It is assessed at 40%. The millage rate is 32 mills. What are the annual taxes?",
    answer: "$5,120",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Property taxes are calculated on assessed value using the millage rate. Step 1: Calculate assessed value: $400,000 × 0.40 = $160,000. Step 2: Convert 32 mills to a decimal: 32 mills = 32 × $0.001 = $0.032 per dollar, or 32 ÷ 1,000 = 0.032. Step 3: Multiply assessed value by millage rate: $160,000 × 0.032 = $5,120. Alternatively: ($160,000 ÷ $1,000) × 32 = $5,120. Remember: taxes are based on assessed value (often a percentage of market value), not the full market value. The millage rate is set by local taxing authorities."
  },
  {
    id: 394,
    question: "A house is sold for $275,300. What is the Georgia transfer tax?",
    answer: "$275.30",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Georgia's transfer tax (also called intangible recording tax on deeds) is $0.10 per $100 of the sales price. This is a Georgia state-mandated tax. Step 1: Divide the sales price by $100: $275,300 ÷ $100 = 2,753. Step 2: Multiply by $0.10: 2,753 × $0.10 = $275.30. Note: The tax is calculated on the full sales price, and if there's a fraction of $100, you still pay the tax on that fraction. This tax is typically paid by the seller at closing and is collected when the deed is recorded. It's separate from the intangibles tax on mortgages."
  },
  {
    id: 395,
    question: "A buyer obtains a loan for $220,150. What is the Georgia intangibles tax?",
    answer: "$661.50",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Georgia's intangibles tax on mortgages is $1.50 per $500 (or fraction) of the loan amount. This tax must be rounded UP to the nearest $500 increment. Step 1: Round the loan amount UP to the nearest $500: $220,150 rounds up to $220,500 (since $220,150 is between $220,000 and $220,500, we round up to $220,500). Step 2: Divide by $500: $220,500 ÷ $500 = 441. Step 3: Multiply by $1.50: 441 × $1.50 = $661.50. This is a Georgia state-mandated tax typically paid by the buyer (borrower) at closing when the mortgage is recorded. It's separate from the transfer tax on deeds."
  },
  {
    id: 396,
    question: "An investor buys a property for $500,000 with a 20% down payment. The property NOI is $35,000. What is the cash-on-cash return?",
    answer: "17.5%",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Cash-on-cash return measures the annual return on the actual cash invested (down payment). Formula: Cash-on-Cash Return = Annual Cash Flow ÷ Cash Invested. Step 1: Calculate the down payment (cash invested): $500,000 × 0.20 = $100,000. Step 2: Note that cash-on-cash return typically uses cash flow AFTER debt service (mortgage payments), not NOI. If the answer is 17.5%, then: $100,000 × 0.175 = $17,500 annual cash flow after debt service. However, if using NOI directly: $35,000 ÷ $100,000 = 35%. The 17.5% answer suggests debt service of $17,500 ($35,000 NOI - $17,500 debt service = $17,500 cash flow). Formula: Cash-on-Cash Return = (NOI - Debt Service) ÷ Cash Invested = $17,500 ÷ $100,000 = 17.5%. This metric shows the return on the investor's actual cash investment, not the total property value."
  },
  {
    id: 397,
    question: "How many linear feet of fencing are needed to enclose a rectangular lot that is 120 feet by 300 feet?",
    answer: "840 feet",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "To find the perimeter (total length of fencing needed), use the formula: Perimeter = 2 × (Length + Width). Step 1: Add the length and width: 120 feet + 300 feet = 420 feet. Step 2: Multiply by 2: 2 × 420 = 840 feet. This gives the total linear feet of fencing needed to enclose the rectangular lot. Linear feet means the total length in a straight line, which is what you need for fencing calculations."
  },
  {
    id: 398,
    question: "A property has PGI of $100,000, vacancy loss of 5%, and operating expenses of $40,000. What is the NOI?",
    answer: "$55,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "NOI (Net Operating Income) is calculated from PGI (Potential Gross Income) by accounting for vacancies and operating expenses. Step 1: Calculate EGI (Effective Gross Income): PGI minus vacancy loss. Vacancy loss = $100,000 × 0.05 = $5,000. EGI = $100,000 - $5,000 = $95,000. Alternatively: EGI = PGI × (1 - Vacancy Rate) = $100,000 × 0.95 = $95,000. Step 2: Calculate NOI: EGI minus Operating Expenses = $95,000 - $40,000 = $55,000. NOI is the income remaining after all operating expenses but before debt service (mortgage payments) and income taxes. It's a key metric for income-producing properties."
  },
  {
    id: 399,
    question: "Can a broker in Georgia have a trust account in a non-Georgia bank?",
    answer: "No, all trust accounts must be in a bank located within Georgia.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This allows GREC proper jurisdiction and oversight."
  },
  {
    id: 400,
    question: "What happens to a salesperson's license if their sponsoring broker dies?",
    answer: "The license is automatically inactivated.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "The agent must transfer to a new broker to reactivate it."
  },
  {
    id: 401,
    question: "A person who has a broker's license but works for another broker.",
    answer: "Associate broker",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "They have the qualifications of a broker but not the supervisory responsibilities."
  },
  {
    id: 402,
    question: "Does GREC mediate commission disputes between licensees?",
    answer: "No, GREC does not get involved in commission disputes.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "These are civil matters to be resolved by the parties or through arbitration/court."
  },
  {
    id: 403,
    question: "To reinstate a license that has been lapsed for 2-5 years in Georgia, what must a licensee do?",
    answer: "Pay all fees and penalties and complete the 25-hour post-license course.",
    category: "Georgia State Laws and Rules",
    difficulty: "hard",
    explanation: "If lapsed over 5 years, they must retake the pre-license course and state exam."
  },
  {
    id: 404,
    question: "What is the penalty for practicing real estate without a license in Georgia?",
    answer: "It is a misdemeanor, subject to fines and cease and desist orders from GREC.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "It can also be prosecuted by the local district attorney."
  },
  {
    id: 405,
    question: "Must a brokerage firm's phone number appear in all advertising?",
    answer: "Yes, the firm's name and phone number must be included.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "The firm's number must be of equal or greater prominence than the agent's."
  },
  {
    id: 406,
    question: "When a licensee transfers firms, what happens to their listings and buyer clients?",
    answer: "The brokerage engagements belong to the original broker, not the agent.",
    category: "Georgia State Laws and Rules",
    difficulty: "medium",
    explanation: "The agent cannot take them without the original broker's written permission."
  },
  {
    id: 407,
    question: "What information must appear on a brokerage engagement agreement?",
    answer: "Names of parties, description of property, terms, expiration date, and commission.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "It must be in writing and signed by all parties."
  },
  {
    id: 408,
    question: "If GREC revokes a license, how long must the individual wait before they can reapply for a new license?",
    answer: "10 years.",
    category: "Georgia State Laws and Rules",
    difficulty: "hard",
    explanation: "Re-licensure after revocation is not guaranteed."
  },
  {
    id: 409,
    question: "A deed given to the purchaser at a court-ordered sale (e.g., tax foreclosure).",
    answer: "Sheriff's Deed",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "It conveys title but has no warranties."
  },
  {
    id: 410,
    question: "A clause in the security deed giving the lender the right to sell upon default.",
    answer: "Power of sale clause",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This allows foreclosure without a court order."
  },
  {
    id: 411,
    question: "The system used by counties to record and index real estate documents.",
    answer: "Deed book and page number",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "Each document is assigned a specific book and page number for retrieval."
  },
  {
    id: 412,
    question: "A provision allowing a surviving spouse/minor children to receive property from an estate.",
    answer: "Year's support",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "This has priority over most other debts of the estate."
  },
  {
    id: 413,
    question: "Are dual contracts legal in Georgia?",
    answer: "No, they are a form of mortgage fraud and are illegal.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This involves creating two contracts for the same transaction to deceive a lender."
  },
  {
    id: 414,
    question: "A law regulating the sale of subdivided land to protect consumers.",
    answer: "Georgia Land Sales Act",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "It often applies to out-of-state land marketed to Georgians."
  },
  {
    id: 415,
    question: "Must a broker have a physical office location in Georgia?",
    answer: "Yes, every qualifying broker must maintain a definite place of business in Georgia.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "The firm's license must be displayed there."
  },
  {
    id: 416,
    question: "A law that sets the maximum interest rate that can be charged on a loan.",
    answer: "Usury law",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "Many first mortgages are exempt from Georgia's usury laws."
  },
  {
    id: 417,
    question: "Historical spousal property rights. Georgia has abolished them.",
    answer: "Dower and curtesy",
    category: "Georgia Real Estate Practice",
    difficulty: "medium",
    explanation: "Surviving spouses are protected by inheritance laws and \"year's support.\""
  },
  {
    id: 418,
    question: "A method of describing property in a subdivision using a lot number on a recorded plat map.",
    answer: "Lot and block system",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "This is the most common way to describe residential lots."
  },
  {
    id: 419,
    question: "A personal judgment against a borrower if a foreclosure sale does not cover the full debt.",
    answer: "Deficiency judgment",
    category: "Finance and Closing",
    difficulty: "hard",
    explanation: "In GA, the lender must get judicial confirmation of the sale to pursue this."
  },
  {
    id: 420,
    question: "A deed given by a borrower to a lender to avoid foreclosure.",
    answer: "Deed in lieu of foreclosure",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The lender must agree to accept it."
  },
  {
    id: 421,
    question: "An account to hold the borrower's funds for future property taxes and insurance.",
    answer: "Escrow account (Impound account)",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The lender collects a portion each month with the mortgage payment."
  },
  {
    id: 422,
    question: "An agreement by a lienholder to accept a lower priority position than a later lien.",
    answer: "Subordination",
    category: "Finance and Closing",
    difficulty: "hard",
    explanation: "For example, a land seller might subordinate their lien to a construction loan."
  },
  {
    id: 423,
    question: "On a closing disclosure, is the loan amount a debit or a credit for the buyer?",
    answer: "A credit.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It is money the buyer is receiving."
  },
  {
    id: 424,
    question: "On a closing disclosure, is the purchase price a debit or a credit for the buyer?",
    answer: "A debit.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It is the main charge the buyer must pay."
  },
  {
    id: 425,
    question: "A loan that allows a borrower to draw funds as needed against their home's equity.",
    answer: "Home equity line of credit (HELOC)",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "It functions like a credit card with the home as collateral."
  },
  {
    id: 426,
    question: "A single mortgage that covers more than one property.",
    answer: "Blanket mortgage",
    category: "Finance and Closing",
    difficulty: "medium",
    explanation: "Often used by developers and includes a partial release clause."
  },
  {
    id: 427,
    question: "A ratio comparing a borrower's monthly debt payments to their gross monthly income.",
    answer: "Debt-to-income ratio (DTI)",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "Lenders use it to assess a borrower's ability to repay a loan."
  },
  {
    id: 428,
    question: "Who is responsible for the accuracy of the Closing Disclosure?",
    answer: "The lender.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The lender must ensure it is accurate and delivered to the borrower on time."
  },
  {
    id: 429,
    question: "What is the difference between an \"\"amendment\"\" and an \"\"addendum\"\"?",
    answer: "An amendment modifies an existing contract; an addendum adds new terms.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Both must be in writing and signed by all parties."
  },
  {
    id: 430,
    question: "Can an offer be revoked?",
    answer: "Yes, the offeror can revoke an offer at any time before it is accepted.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "The revocation must be communicated to the offeree."
  },
  {
    id: 431,
    question: "Forcing someone to enter into a contract through unlawful threat or force.",
    answer: "Duress",
    category: "Contracts",
    difficulty: "easy",
    explanation: "A contract signed under duress is voidable by the victim."
  },
  {
    id: 432,
    question: "Using a position of trust to unfairly persuade someone into a contract.",
    answer: "Undue influence",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This makes the contract voidable by the influenced party."
  },
  {
    id: 433,
    question: "Seller financing where the seller holds title until the buyer pays the full price.",
    answer: "Land contract (Contract for deed)",
    category: "Contracts",
    difficulty: "medium",
    explanation: "The buyer has equitable title and possession during the payment period."
  },
  {
    id: 434,
    question: "What is the difference between \"\"joint tenancy\"\" and \"\"tenancy in common\"\"?",
    answer: "Joint tenancy has the right of survivorship; tenancy in common does not.",
    category: "Property Ownership",
    difficulty: "easy",
    explanation: "A joint tenant's share goes to co-owners; a tenant in common's share goes to heirs."
  },
  {
    id: 435,
    question: "The person designated to receive the property after the life tenant dies.",
    answer: "Remainderman",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "The remainderman holds a future interest in the property."
  },
  {
    id: 436,
    question: "A right or privilege that is attached to and transfers with the land.",
    answer: "Appurtenance",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Examples include easements and water rights."
  },
  {
    id: 437,
    question: "Ownership of stock in a corporation that owns a building, which gives the right to a proprietary lease.",
    answer: "Cooperative (co-op)",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Owners do not hold individual title to their units."
  },
  {
    id: 438,
    question: "Can a corporation own property in \"\"joint tenancy\"\"?",
    answer: "No, because a corporation does not die and cannot have a right of survivorship.",
    category: "Property Ownership",
    difficulty: "hard",
    explanation: "A corporation can own property in severalty or as a tenant in common."
  },
  {
    id: 439,
    question: "When a tenant wrongfully remains in possession after their lease expires.",
    answer: "Estate at sufferance",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "The tenant is a \"holdover tenant.\""
  },
  {
    id: 440,
    question: "What are the three main types of maintenance a property manager handles?",
    answer: "Preventive, corrective, and routine maintenance.",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "A good manager focuses on preventive maintenance to reduce future costs."
  },
  {
    id: 441,
    question: "The ADA requires accessibility in public accommodations and commercial facilities.",
    answer: "Americans with Disabilities Act (ADA)",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "Managers must ensure properties comply with requirements like ramps and accessible restrooms."
  },
  {
    id: 442,
    question: "A major retail tenant in a shopping center that attracts customers.",
    answer: "Anchor tenant",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "Examples include department stores or grocery stores."
  },
  {
    id: 443,
    question: "In Georgia, what is the landlord's duty regarding property condition?",
    answer: "Landlords must keep the premises in a state of good repair.",
    category: "Leasing & Property Management",
    difficulty: "easy",
    explanation: "This duty to maintain a habitable property cannot be waived."
  },
  {
    id: 444,
    question: "A federal law that sets rules for commercial email.",
    answer: "CAN-SPAM Act",
    category: "Practice of Real Estate",
    difficulty: "medium",
    explanation: "It requires an opt-out option and a valid postal address."
  },
  {
    id: 445,
    question: "A law restricting telemarketing calls and establishing the Do-Not-Call Registry.",
    answer: "Telephone Consumer Protection Act (TCPA)",
    category: "Practice of Real Estate",
    difficulty: "medium",
    explanation: "Licensees must check the registry before making cold calls."
  },
  {
    id: 446,
    question: "Errors and Omissions insurance; a form of professional liability insurance.",
    answer: "E&O insurance",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "It protects licensees from claims of mistakes and negligence."
  },
  {
    id: 447,
    question: "Can a licensee give legal advice?",
    answer: "No, this is the unauthorized practice of law.",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Licensees must advise clients to consult an attorney for legal questions."
  },
  {
    id: 448,
    question: "What is the difference between a \"\"REALTOR®\"\" and a real estate agent?",
    answer: "A real estate agent is licensed by the state; a REALTOR® is a member of the NAR.",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "REALTORS® must adhere to the NAR's Code of Ethics."
  },
  {
    id: 449,
    question: "An appraisal done without a physical inspection of the property.",
    answer: "Desk review appraisal",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "The appraiser relies on public records and other available data."
  },
  {
    id: 450,
    question: "The principle that the value of an improvement is based on its contribution to the whole property's value, not its cost.",
    answer: "Principle of contribution",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "A $50,000 pool might only add $20,000 to the property's value."
  },
  {
    id: 451,
    question: "A method of converting future income into present value by discounting each future payment.",
    answer: "Yield capitalization",
    category: "Valuation & Market Analysis",
    difficulty: "hard",
    explanation: "A more complex method than direct capitalization (IRV)."
  },
  {
    id: 452,
    question: "The ethical and performance standards for appraisers.",
    answer: "USPAP (Uniform Standards of Professional Appraisal Practice)",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "The ethical and performance standards for appraisers."
  },
  {
    id: 453,
    question: "The economic characteristics of a location that affect a property's value.",
    answer: "Situs",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "Often summarized as \"location, location, location.\""
  },
  {
    id: 454,
    question: "Combining the possession periods of successive adverse possessors to meet the statutory requirement.",
    answer: "Tacking",
    category: "Transfer of Title",
    difficulty: "hard",
    explanation: "There must be a direct connection (privity) between the possessors."
  },
  {
    id: 455,
    question: "A land registration system where the state guarantees title. Not used in Georgia.",
    answer: "Torrens system",
    category: "Transfer of Title",
    difficulty: "hard",
    explanation: "Georgia uses the recording system."
  },
  {
    id: 456,
    question: "A written amendment to a will.",
    answer: "Codicil",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "It must be executed with the same formalities as the original will."
  },
  {
    id: 457,
    question: "When title passes to the heirs of someone who dies without a will (intestate).",
    answer: "Title by descent",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "This is also known as intestate succession."
  },
  {
    id: 458,
    question: "A condensed history of all recorded documents affecting a property's title.",
    answer: "Abstract of title",
    category: "Transfer of Title",
    difficulty: "easy",
    explanation: "An attorney reviews the abstract to give an opinion of title."
  },
  {
    id: 459,
    question: "A defense for a landowner who acquired property after contamination and did not know about it after conducting due diligence.",
    answer: "Innocent landowner defense",
    category: "Property Disclosures",
    difficulty: "hard",
    explanation: "This encourages buyers to perform environmental site assessments."
  },
  {
    id: 460,
    question: "Are electromagnetic fields (EMFs) considered a hazard that must be disclosed?",
    answer: "There is no federal requirement to disclose EMFs, but they can be a buyer concern.",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "Disclosure is a matter of state law and risk management."
  },
  {
    id: 461,
    question: "Sealing off asbestos-containing materials to prevent fibers from becoming airborne.",
    answer: "Encapsulation",
    category: "Property Disclosures",
    difficulty: "easy",
    explanation: "This is often a safer and cheaper alternative to removal (abatement)."
  },
  {
    id: 462,
    question: "Under BRRETA, must a broker disclose information about the neighborhood, such as crime rates or school quality?",
    answer: "No, brokers are not required to discover or disclose neighborhood conditions.",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "Brokers should direct clients to reliable third-party sources."
  },
  {
    id: 463,
    question: "A federal law that regulates the discharge of pollutants into U.S. waters.",
    answer: "Clean Water Act",
    category: "Property Disclosures",
    difficulty: "medium",
    explanation: "It is important for properties near water or wetlands."
  },
  {
    id: 464,
    question: "Changing a property's zoning to a less intensive or less valuable use.",
    answer: "Downzoning",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "Example: changing commercial zoning to residential."
  },
  {
    id: 465,
    question: "A government document certifying a building is compliant with codes and fit for occupancy.",
    answer: "Certificate of occupancy (CO)",
    category: "Land Use Controls",
    difficulty: "easy",
    explanation: "A CO is required before a new building can be occupied."
  },
  {
    id: 466,
    question: "A development with a mix of land uses and flexible zoning standards.",
    answer: "Planned unit development (PUD)",
    category: "Land Use Controls",
    difficulty: "medium",
    explanation: "PUDs often include common areas and are managed by an HOA."
  },
  {
    id: 467,
    question: "Zoning that requires a percentage of new housing to be affordable.",
    answer: "Inclusionary zoning",
    category: "Land Use Controls",
    difficulty: "medium",
    explanation: "A tool used to create affordable housing."
  },
  {
    id: 468,
    question: "Illegally singling out one parcel for a different zoning classification that is inconsistent with the surrounding area.",
    answer: "Spot zoning",
    category: "Land Use Controls",
    difficulty: "hard",
    explanation: "This is illegal because it is not in the public's best interest."
  },
  {
    id: 469,
    question: "What is the statute of limitations for damage to realty in Georgia?",
    answer: "4 years.",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "A lawsuit for trespass or damage to real property must be filed within this period."
  },
  {
    id: 470,
    question: "The starting and ending point for a metes and bounds description.",
    answer: "Point of beginning (POB)",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The description must \"\"close\"\" by returning to the POB."
  },
  {
    id: 471,
    question: "Can a client fire their agent for any reason?",
    answer: "Yes, a client has the power to terminate an agency relationship, but they may be in breach of contract.",
    category: "Agency",
    difficulty: "medium",
    explanation: "They may still owe a commission if they terminate the agreement early."
  },
  {
    id: 472,
    question: "A letter sent to a non-performing party setting a new, firm deadline.",
    answer: "Time is of the essence letter",
    category: "Contracts",
    difficulty: "hard",
    explanation: "It states that failure to meet the new deadline will be a material breach."
  },
  {
    id: 473,
    question: "A life estate measured by the life of someone other than the life tenant (\"\"for the life of another\"\").",
    answer: "Pur autre vie life estate",
    category: "Property Ownership",
    difficulty: "hard",
    explanation: "Example: Ann has a life estate for as long as Bob is alive."
  },
  {
    id: 474,
    question: "A report that confirms whether the value in a previous appraisal is still valid.",
    answer: "Appraisal update",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "It is shorter and less expensive than a full appraisal."
  },
  {
    id: 475,
    question: "What is the difference between \"\"fraud\"\" and \"\"negligence\"\"?",
    answer: "Fraud is intentional deceit; negligence is an unintentional mistake or failure of due care.",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Fraud involves intent, while negligence does not."
  },
  {
    id: 476,
    question: "Does a licensee need to disclose if they are related to the buyer or seller?",
    answer: "Yes, under BRRETA, a licensee must disclose any familial relationship with a party.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "This is considered a material fact and must be in writing."
  },
  {
    id: 477,
    question: "A transaction where an owner sells a property and then leases it back from the new owner.",
    answer: "Sale-leaseback",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "This allows the seller to free up capital while still using the property."
  },
  {
    id: 478,
    question: "A property's after-tax cash flow is $10,000 and the initial equity was $80,000. What is the after-tax return on equity?",
    answer: "12.5%",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "After-tax return on equity measures the percentage return on the investor's equity investment after all expenses and taxes. Formula: Return on Equity = After-Tax Cash Flow ÷ Initial Equity. Step 1: Divide after-tax cash flow by initial equity: $10,000 ÷ $80,000 = 0.125. Step 2: Convert to percentage: 0.125 × 100 = 12.5%. This metric shows how well the investor's equity is performing. After-tax cash flow is the money remaining after all operating expenses, debt service (mortgage payments), and income taxes. Initial equity is the down payment plus any additional capital invested."
  },
  {
    id: 479,
    question: "A mortgage with a history of timely payments.",
    answer: "Seasoned mortgage",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "These loans are considered lower risk and are more attractive on the secondary market."
  },
  {
    id: 480,
    question: "A clause in a listing agreement that excludes a specific, named buyer from the commission agreement.",
    answer: "Waiver of broker protection clause",
    category: "Agency",
    difficulty: "medium",
    explanation: "Used when the seller has already been negotiating with a potential buyer."
  },
  {
    id: 481,
    question: "The interest a buyer holds in a property after signing a sales contract but before closing.",
    answer: "Equitable title",
    category: "Contracts",
    difficulty: "medium",
    explanation: "It gives the buyer the right to obtain legal title at closing."
  },
  {
    id: 482,
    question: "How can an easement be terminated?",
    answer: "By merger, release, abandonment, or non-use (prescription).",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "Merger occurs if the owner of the dominant estate buys the servient estate."
  },
  {
    id: 483,
    question: "Who administers the Georgia Land Sales Act?",
    answer: "The Georgia Secretary of State.",
    category: "Georgia Real Estate Practice",
    difficulty: "hard",
    explanation: "The act protects consumers from fraudulent land sale schemes."
  },
  {
    id: 484,
    question: "A method of calculating depreciation: Effective Age / Total Economic Life.",
    answer: "Age-life method of depreciation",
    category: "Valuation & Market Analysis",
    difficulty: "medium",
    explanation: "If a building's effective age is 10 and its economic life is 50, it's 20% depreciated."
  },
  {
    id: 485,
    question: "The main purpose is to provide a standard of conduct for REALTORS® that is higher than the law.",
    answer: "NAR Code of Ethics",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "It promotes professionalism, integrity, and competency."
  },
  {
    id: 486,
    question: "Can an unlicensed person host an open house in Georgia?",
    answer: "No, hosting an open house requires a license.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "An unlicensed assistant can only perform clerical tasks at an open house."
  },
  {
    id: 487,
    question: "A long-term lease of land, on which the tenant can build improvements.",
    answer: "Ground lease",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "At the end of the lease, the improvements typically revert to the landowner."
  },
  {
    id: 488,
    question: "A commission of $14,000 was paid on a sale. The commission rate was 7%. What was the sales price?",
    answer: "$200,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "This is a reverse commission calculation - finding the sales price when you know the commission amount and rate. Formula: Sales Price = Commission ÷ Commission Rate (as decimal). Step 1: Convert 7% to a decimal: 7% = 0.07. Step 2: Divide the commission by the commission rate: $14,000 ÷ 0.07 = $200,000. To verify: $200,000 × 0.07 = $14,000. This type of calculation is useful when you know the commission paid and need to determine what the property sold for."
  },
  {
    id: 489,
    question: "A form of seller financing where a new loan wraps around an existing loan.",
    answer: "Wraparound mortgage",
    category: "Finance and Closing",
    difficulty: "hard",
    explanation: "The buyer pays the seller, who then pays the original lender."
  },
  {
    id: 490,
    question: "Does the duty of \"\"confidentiality\"\" survive the termination of an agency relationship?",
    answer: "Yes, the duty of confidentiality is eternal.",
    category: "Agency",
    difficulty: "easy",
    explanation: "An agent can never disclose a client's confidential information."
  },
  {
    id: 491,
    question: "An agreement by both parties to cancel a contract.",
    answer: "Mutual rescission",
    category: "Contracts",
    difficulty: "easy",
    explanation: "This returns the parties to their original positions."
  },
  {
    id: 492,
    question: "The system in the U.S. where individuals have absolute ownership of land.",
    answer: "Allodial system",
    category: "Property Ownership",
    difficulty: "medium",
    explanation: "This is in contrast to the feudal system where a king owned all the land."
  },
  {
    id: 493,
    question: "If a licensee has a trust account, how often must they reconcile it and provide a report to their broker?",
    answer: "At least once per month.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "The reconciliation must be in writing."
  },
  {
    id: 494,
    question: "An appraisal based on an exterior-only inspection of the property.",
    answer: "Drive-by appraisal",
    category: "Valuation & Market Analysis",
    difficulty: "easy",
    explanation: "It is a limited appraisal and less reliable than a full appraisal."
  },
  {
    id: 495,
    question: "A law requiring law enforcement to make information on registered sex offenders public.",
    answer: "Megan's Law",
    category: "Practice of Real Estate",
    difficulty: "easy",
    explanation: "Agents should direct clients to these public resources, not provide the information themselves."
  },
  {
    id: 496,
    question: "Must a CAM (Community Association Manager) licensee be affiliated with a broker?",
    answer: "Yes, a CAM license must be placed with a qualifying broker.",
    category: "Georgia State Laws and Rules",
    difficulty: "easy",
    explanation: "A CAM cannot operate independently without a broker's license."
  },
  {
    id: 497,
    question: "A contractual right to buy a property by matching any offer the owner receives.",
    answer: "Right of first refusal",
    category: "Leasing & Property Management",
    difficulty: "medium",
    explanation: "It is often included in a lease."
  },
  {
    id: 498,
    question: "How many acres is 1/4 of a section?",
    answer: "160 acres",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "A section is a standard unit of land measurement in the U.S. Public Land Survey System. One full section equals 640 acres. To find 1/4 of a section: 640 acres ÷ 4 = 160 acres. Alternatively: 640 × 0.25 = 160 acres. A quarter section (1/4 section) is also called a 'quarter' and is a common land division. Other common divisions: 1/2 section = 320 acres, 1/8 section (half of a quarter) = 80 acres. This measurement system is used primarily in states west of the original 13 colonies."
  },
  {
    id: 499,
    question: "What is the difference between the \"\"note rate\"\" and the \"\"APR\"\"?",
    answer: "The note rate is the simple interest rate; the APR is the effective rate including loan costs.",
    category: "Finance and Closing",
    difficulty: "easy",
    explanation: "The APR is the true cost of borrowing and is better for comparing loans."
  },
  {
    id: 500,
    question: "What is the primary role of the closing attorney in a typical Georgia real estate transaction?",
    answer: "To represent the lender.",
    category: "Georgia Real Estate Practice",
    difficulty: "easy",
    explanation: "The attorney ensures the title is clear and the lender's security interest is protected."
  }
];